/**
 * @file This file contains the UIManager class, which handles all direct DOM manipulation
 * for the game, including updating the grid, displaying messages, and managing UI pages. 
 * @module UIManager - edited
 */
import { buildGameConfig} from '../lib/utils.js';
import { temporaryEffectsConfig } from '../config/temporaryEffectsConfig.js';
import * as templates from '../ui/viewTemplates.js';
import { getNpcWanderingToggleHtml, getWeatherEffectsToggleHtml, getAmbientAnimalsToggleHtml, get3dEffectsToggleHtml, getColorblindModeToggleHtml  } from '../ui/viewTemplates.js';
import { featuresConfig } from '../config/featuresConfig.js';
import { getPromptsConfig } from '../config/promptsConfig.js';
import { GAME_TYPE_QUICK_CLICK, GAME_TYPE_CAMPAIGN_CLICK, GAME_TYPE_CAMPAIGN_ROLLING } from '../constants/gameTypes.js';
import { PageManager } from '../ui/PageManager.js';
//
/**
 * Manages all direct DOM manipulation for the game, acting as the interface
 * between the game logic and the HTML document.
 *
 * @export
 * @class UIManager
 * @typedef {UIManager} 
 */
export class UIManager {
    /**
     * @param {object} dependencies - The dependencies for the UIManager.
     * @param {object} dependencies.dom - An object containing references to various DOM elements.
     * @param {object} dependencies.textConfig - The text configuration for game messages and UI labels.
     * @param {object} dependencies.biomesConfig - The biomes configuration.
     * @param {object} dependencies.masterItemsConfig - The master items configuration.
     * @param {object} dependencies.baseConfig - The base game configuration.
     * @param {function(string): void} dependencies.playAudio - Function to play audio.
     * @param {import('./BiomeManager.js').BiomeManager} dependencies.biomeManager - The biome manager instance.
     * @param {import('./SaveManager.js').SaveManager} dependencies.saveManager - The save manager instance.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The event emitter instance.
     * @param {import('./PlayerStatsManager.js').PlayerStatsManager} dependencies.playerStatsManager - The player stats manager instance.
     * @param {import('./PreferencesManager.js').PreferencesManager} dependencies.preferencesManager - The preferences manager instance.
     * @param {object} dependencies.achievementsConfig - The achievements configuration object.
     * @param {import('./DebugManager.js').DebugManager} dependencies.debugManager - The debug manager instance.
     */
    // ... (constructor remains the same)
    constructor(dependencies) {
        /** @private */
        this.dom = dependencies.dom;
        /** @private */
        this.textConfig = dependencies.textConfig;
        this.dom.miniMergeControls = document.getElementById('mini-merge-controls');
        /** @private */
        this.biomesConfig = dependencies.biomesConfig;
        /** @private */
        this.masterItemsConfig = dependencies.masterItemsConfig;
        /** @private */
        this.baseConfig = dependencies.baseConfig;
        /** @private */
        this.gameOptions = dependencies.gameOptions;
        /** @private */
        this.playAudio = dependencies.playAudio;
        /** @private */
        this.biomeManager = dependencies.biomeManager;
        /** @private */
        this.playerStatsManager = dependencies.playerStatsManager;
        /** @private */
        this.preferencesManager = dependencies.preferencesManager;
        /** @private */
        this.eventEmitter = dependencies.eventEmitter;
        /** @private */
        this.achievementsConfig = dependencies.achievementsConfig;
        /** @private */
        this.saveManager = dependencies.saveManager;
        /** @private @type {Map<string, object>} */
        this.temporaryEffects = new Map();
        /** @private */
        this.debugManager = dependencies.debugManager;
        /** @private @type {function(Event): void|null} */
        this.currentClickHandler = () => {}; // Initialize with a no-op function
        /** @private */
        this.configManager = dependencies.configManager;
        /** @private @type {number|null} */
        this.gridSize = null;
        /** @private @type {HTMLElement|null} */
        this.rtsGuiElement = null;
        /** @private @type {{element: HTMLElement, npcId: number, managerType: string} | null} */
        this.activeNpcTooltip = null;
        /** @private @type {boolean} */
        this.isSpottingModeActive = false;
        /** @private @type {HTMLElement|null} */
        this.spottingOverlay = null;
        /** @private @type {{width: number, height: number}} */
        this.cachedCellDimensions = { width: 0, height: 0 };
        /** @private @type {number} */
        this.lastGridWidth = 0;
        this.boundHandleSpottingMouseMove = this._handleSpottingMouseMove.bind(this);
        this.boundHandleSpottingClick = this._handleSpottingClick.bind(this);

        /**
         * Stores the currently selected settings for quick game.
         * @type {object}
         */
        this.quickGameSelectedSettings = {};

        /**
         * Stores the currently selected settings for a new campaign.
         * @type {object}
         */
        this.campaignSelectedSettings = {};

        this.miniMergeSelectedSettings = {};

        /**
         * Flag to defer collector creation until the grid is visible.
         * @private
         */
        this._collectorNeedsCreation = false;

        /**
         * Flag to defer obstacle rendering until the grid is visible.
         * @private
         */
        this._obstaclesNeedRendering = false;

        /**
         * Flag to defer road rendering until the grid is visible.
         * @private
         */
        this._roadsNeedRendering = false;

        /** @private */
        this.pageManager = new PageManager({
            dom: this.dom,
            playAudio: this.playAudio,
            eventEmitter: this.eventEmitter,
            configManager: this.configManager,
            biomeManager: this.biomeManager,
            saveManager: this.saveManager,
            showPrompt: this.showPrompt.bind(this),
            achievementsConfig: this.achievementsConfig,
            showAchievementToast: this.showAchievementToast.bind(this),
            textConfig: this.textConfig,
            masterItemsConfig: this.masterItemsConfig,
            baseConfig: this.baseConfig,
            gameOptions: this.gameOptions,
            playerStatsManager: this.playerStatsManager,
            preferencesManager: this.preferencesManager,
            createCollector: this.createCollector.bind(this),
            quickGameSelectedSettings: this.quickGameSelectedSettings,
            campaignSelectedSettings: this.campaignSelectedSettings
        });

        this.eventEmitter.on('audio:muteStateChanged', ({ isMuted }) => {
            this.updateMuteButtonText(isMuted);
        });

        this.eventEmitter.on('ui:showAchievements', () => this.pageManager.showAchievementsPage());
        this.eventEmitter.on('ui:showQuickGameSettings', () => this.showQuickGameSettings());
        this.eventEmitter.on('ui:showMiniMergeSettings', () => this.showMiniMergeSettings());
        this.eventEmitter.on('ui:showAboutPage', () => this.showAboutPage());
        this.eventEmitter.on('ui:showCampaignSettings', () => this.showCampaignSettings());

        // Listen for NPC hover events
        this._makeOverlaysClickThrough();
        this._setupNpcTooltipListeners();

        // Add a click listener to the NPC selector to force an update.
        this.dom.npcSelector.addEventListener('click', () => {
            this.eventEmitter.emit('ui:requestNpcListUpdate');
        });

        this.eventEmitter.on('ui:showNpcTooltip', ({ npc, managerType }) => this.showNpcTooltip(npc, managerType));
        this.dom.npcSelector.addEventListener('change', (e) => {
            const selectedOption = e.target.options[e.target.selectedIndex];
            const npcId = parseInt(selectedOption.value, 10);
            const managerType = selectedOption.dataset.managerType;
            this.eventEmitter.emit('ui:requestNpcEditor', { npcId, managerType });
        });
        this.dom.npcEditorBox.addEventListener('mouseenter', () => {
            this.eventEmitter.emit('ui:pauseNpcsForEditing', true);
        });
        this.dom.npcEditorBox.addEventListener('mouseleave', () => {
            this.eventEmitter.emit('ui:pauseNpcsForEditing', false);
            this.dom.npcEditorContent.classList.add('hidden');
            this._clearNpcEditorHighlight();
        });
        this.dom.settlementSelector.addEventListener('change', (e) => {
            const settlementKey = e.target.value;
            if (settlementKey) {
                this.eventEmitter.emit('ui:requestSettlementEditor', { settlementKey });
            } else {
                this.dom.settlementEditorContent.classList.add('hidden');
            }
        });
        this.dom.settlementEditorBox.addEventListener('mouseenter', () => {
            this.eventEmitter.emit('ui:pauseNpcsForEditing', true);
        });
        this.dom.settlementEditorBox.addEventListener('mouseleave', () => {
            this.eventEmitter.emit('ui:pauseNpcsForEditing', false);
            this.dom.settlementEditorContent.classList.add('hidden');
        });

        // --- New Dev Tools Listeners ---
        this.dom.buttons.showSpawnableBtn.addEventListener('click', () => {
            this.eventEmitter.emit('ui:requestSpawnableAccessories');
        });
        this.dom.buttons.showExistingBtn.addEventListener('click', () => {
            this.eventEmitter.emit('ui:requestLiveEntityCounts');
        });
        this.dom.buttons.showRecyclableBtn.addEventListener('click', () => {
            this.eventEmitter.emit('ui:requestRecyclableAccessories');
        });
        this.dom.devToolsModalCloseBtn.addEventListener('click', () => {
            this.dom.devToolsModal.classList.add('hidden');
        });

        this.dom.miniMergeControls.querySelector('#nudge-mini-merge-btn').addEventListener('click', () => {
            this.eventEmitter.emit('ui:miniMergeNudge');
        });

        // Add a delegated listener for the queue in Mini Merge mode
        this.dom.queue.addEventListener('click', (e) => {
            const queueItem = e.target.closest('.queue-gem');
            if (queueItem && queueItem.dataset.index) {
                const index = parseInt(queueItem.dataset.index, 10);
                this.eventEmitter.emit('ui:miniMergeQueueClick', index);
            }
        });

        this.eventEmitter.on('game:spawnableAccessoriesList', ({ accessories }) => {
            this._displayAccessoryListInModal('Spawnable Accessories', accessories);
        });

        this.eventEmitter.on('game:liveEntityCounts', ({ counts }) => {
            const combinedItems = { ...counts.itemsInInventories, ...counts.itemsHeldByNpcs };
            this._displayAccessoryListInModal('Accessories In World', combinedItems, true);
        });
        this.eventEmitter.on('game:recyclableAccessoriesList', ({ accessories }) => {
            this._displayAccessoryListInModal('Recyclable Accessories', accessories);
        });


        // Asynchronously set up pages that require external content
        this.pageManager.setupAboutPage();

        this._apply3dEffectsPreference(); // Apply on initial load
this._createSpottingModeElements();
    }

   

    // --- Page Display & Lifecycle ---

    /**
     * Injects CSS for the RTS GUI to make it larger on high-resolution screens.
     * Applies 'pointer-events: none' to all overlay elements so they don't
     * block clicks from reaching the grid underneath.
     * @private
     */
    _makeOverlaysClickThrough() {
        const overlays = [
            this.dom.npcOverlay,
            this.dom.nonHumanNPCOverlay,
            this.dom.temporaryEffectsOverlay,
            this.dom.overlays.roadOverlay,
            this.dom.overlays.obstacleOverlay,
            this.dom.weatherOverlay, // This should be one of the top layers
            this.dom.disasterOverlay,
            this.dom.ambientAnimalOverlay
        ];
        overlays.forEach(overlay => { if (overlay) overlay.style.pointerEvents = 'none'; });
    }

    /**
     * Creates and appends the spotting mode UI elements (toggle button and overlay).
     * @private
     */
    _createSpottingModeElements() {
        // Find the Game Tools container in the DOM
        const toolsContainer = this.dom.gameToolsContainer;

        // Create Save Game button
        const saveButton = document.createElement('button');
        saveButton.id = 'tools-save-btn';
        saveButton.className = 'button-style';
        saveButton.innerHTML = '💾'; // Floppy disk emoji for save
        saveButton.title = 'Save Game';
        saveButton.addEventListener('click', () => {
            this.eventEmitter.emit('ui:saveGame');
            this.playAudio('click');
        });
        // The title is a child, so we need to insert the button before other children.
        toolsContainer.insertBefore(saveButton, toolsContainer.children[1]);


        // Create Exit Control Mode button (hidden by default)
        const exitControlButton = document.createElement('button');
        exitControlButton.id = 'exit-control-mode-btn';
        exitControlButton.className = 'button-style button-danger hidden'; // Start hidden
        exitControlButton.innerHTML = '🛑'; // Stop sign emoji
        exitControlButton.title = 'Release NPC Control';
        exitControlButton.addEventListener('click', () => {
            this.eventEmitter.emit('ui:exitNpcControl');
            this.playAudio('click');
        });
        // Insert it after the title but before other tools for prominence
        toolsContainer.insertBefore(exitControlButton, toolsContainer.children[1]);


        // Create Clear Tags button
        const clearTagsButton = document.createElement('button');
        clearTagsButton.id = 'tools-clear-tags-btn';
        clearTagsButton.className = 'button-style';
        clearTagsButton.innerHTML = '🚫'; // No Entry Sign emoji
        clearTagsButton.title = 'Clear All Tags';
        clearTagsButton.addEventListener('click', () => {
            this.eventEmitter.emit('ui:clearAllTags');
        });
        toolsContainer.insertBefore(clearTagsButton, toolsContainer.children[2]);
        // Create Spotting Mode toggle button
        const spottingButton = document.createElement('button');
        spottingButton.id = 'spotting-toggle-btn';
        spottingButton.className = 'button-style';
        spottingButton.innerHTML = '🔭'; // Binoculars emoji
        spottingButton.title = 'Toggle Spotting Mode';
        spottingButton.addEventListener('click', () => this.toggleSpottingMode());
        toolsContainer.insertBefore(spottingButton, toolsContainer.children[3]);

        // Create the overlay for the binocular effect
        this.spottingOverlay = document.createElement('div');
        this.spottingOverlay.id = 'spotting-overlay';
        this.dom.appContainer.append(this.spottingOverlay);
    }

    /**
     * Toggles the visibility of the "Exit NPC Control" button.
     * @param {boolean} show - True to show the button, false to hide it.
     */
    toggleExitControlModeButton(show) {
        const button = document.getElementById('exit-control-mode-btn');
        if (button) {
            button.classList.toggle('hidden', !show);
        }
    }

    /**
     * Main update loop for the UI, called by the game loop.
     * Manages the lifecycle of temporary effects.
     */
    updateTemporaryEffectsLifecycle() { 
        const now = Date.now();
        for (const [id, effect] of this.temporaryEffects.entries()) {
            if (effect.fading) {
                const fadeDuration = effect.fadeDuration || 500; // Default fade duration
                if (now >= effect.fadeStartTime + fadeDuration) {
                    // The effect has completely faded. Remove it from the map and the DOM.
                    this.temporaryEffects.delete(id);
                    const effectElement = document.getElementById(id);
                    if (effectElement) effectElement.remove();
                }
                continue; // Don't process further if it's already fading
            }
            if (effect.duration && (now >= effect.startTime + effect.duration)) {
                effect.fading = true;
                effect.fadeStartTime = now;
            }
        }
        this.updateTemporaryEffects();
    }

    /**
     * Shows a specific UI page and hides all others.
     * @param {string} pageKey - The key of the page to show (e.g., 'game', 'settings').
     */
    showPage(pageKey) {
        Object.values(this.dom.pages).forEach(page => page.classList.add('hidden'));
        this.dom.pages[pageKey].classList.remove('hidden');
        this.playAudio('click');

        if (pageKey === 'game' && this._collectorNeedsCreation) {
            this.createCollector(this.gridSize);
            this._collectorNeedsCreation = false;
        }
        if (pageKey === 'game' && this._obstaclesNeedRendering) {
            // This assumes that the obstacles data has already been set.
            this.renderObstacles(this._obstaclesToRender);
            this._obstaclesNeedRendering = false;
        }
        if (pageKey === 'game' && this._roadsNeedRendering) {
            // This assumes that the roads data has already been set.
            this.renderRoads(this._roadsToRender);
            this._roadsNeedRendering = false;
        }
        this.eventEmitter.emit('ui:pageChanged', { pageKey });
    }

    /**
     * Displays the quick game settings page.
     */
    showQuickGameSettings() {
        this.configManager.resetToDefaults(); // Reset to defaults before showing settings
        const currentSettings = this.configManager.getSettings();
        this.biomeManager.applyBiome(currentSettings.biomeKey);
        this.displayQuickGameSettings(
            this.configManager.getBiomes(),
            this.configManager.getAvailableGridSizes(),
            currentSettings
        );
        this.showPage('quickGameSettings');
    }

    /**
     * Displays the about page.
     */
    showAboutPage() {
        this.pageManager.showPage('about');
    }

    /**
     * Displays the new campaign settings page.
     */
    showCampaignSettings() {
        // Initialize with default settings
        this.displayCampaignSettings({
            gameMode: 'linear',
            isAnimalsEnabled: true,
            isDisastersEnabled: true,
            isRandomQueueEnabled: true,
            isStationaryObjectsEnabled: true,
            isPowerupsEnabled: true
        });
        this.showPage('campaignSettings');
    }

    showMiniMergeSettings() {
        this.configManager.resetToDefaults();
        const currentSettings = this.configManager.getSettings();
        this.biomeManager.applyBiome(currentSettings.biomeKey);
        this.displayMiniMergeSettings(
            this.configManager.getBiomes(),
            currentSettings
        );
        this.showPage('miniMergeSettings');
    }

    displayMiniMergeSettings(biomes, currentSettings) {
        const pageContainer = this.dom.pages.miniMergeSettings;
        if (!pageContainer) return;

        let contentContainer = pageContainer.querySelector('#mini-merge-settings-content');
        if (!contentContainer) return;

        this.miniMergeSelectedSettings = { ...currentSettings };

        const refresh = () => {
            contentContainer.innerHTML = `
                <h3>${this.textConfig.guide.biomesTitle}</h3>
                ${templates.getBiomesHtml(biomes, this.miniMergeSelectedSettings.biomeKey)}
            `;
        };

        if (!contentContainer.dataset.listenerAttached) {
            contentContainer.addEventListener('click', (e) => {
                let needsRefresh = false;
                const button = e.target.closest('button');
                if (button) {
                    if (button.dataset.biome) {
                        this.miniMergeSelectedSettings.biomeKey = button.dataset.biome;
                        this.biomeManager.applyBiome(this.miniMergeSelectedSettings.biomeKey);
                        needsRefresh = true;
                    }
                }

                if (needsRefresh) refresh();
            });
            contentContainer.dataset.listenerAttached = 'true';
        }

        refresh();
    }

    /**
     * Checks for existing manual save games and shows the appropriate prompt
     * (e.g., continue, new game, multi-save) or the intro page if no saves exist.
     */
    showInitialPageOrPrompt() {
        const quickSave = this.saveManager.getSaveSlotByType(GAME_TYPE_QUICK_CLICK);
        const campaignSave = this.saveManager.getSaveSlotByType(GAME_TYPE_CAMPAIGN_CLICK);

        if (quickSave && campaignSave) {
            this.showPrompt('multiSavePrompt', { quickSave, campaignSave });
        } else if (quickSave) {
            this.showPrompt('continuePrompt', { quickSave });
        }
        else if (campaignSave) {
            this.showPrompt('campaignPrompt', { campaignSave });
        }
        else {
            this.showPage('intro');
        }
    }
 /**
     * Special grid update function for Mini Merge mode.
     * @param {Array<object|null>} slots - The three items in the slots.
     * @param {Array<boolean>} held - The held status of the three slots.
     * @param {object} config - The active game configuration.
     */
    updateMiniMergeGrid(slots, held, config) {
        if (!this.gridSize) return; // Grid not ready

        const handSize = 3;
        const handStartCol = Math.floor((this.gridSize - handSize) / 2);
        const handRow = Math.floor((this.gridSize - 1) / 2);

        for (let i = 0; i < 3; i++) {
            const col = handStartCol + i;
            const gridIndex = handRow * this.gridSize + col;
            const cellElement = this.dom.grid.children[gridIndex];
            const item = slots[i];

            this.updateGridCell(cellElement, item, config);

            // Update held status
            cellElement.classList.toggle('held', held[i]);
        }
    }



    // --- Page Setup & Content Population ---

    /**
     * Sets up the guide page, loading content and populating dynamic data.
     * @param {object} biomes - The biomes configuration object.
     * @param {object} baseConfig - The base game configuration.
     * @param {object} masterItemsConfig - The master items configuration.
     * @param {Array<object>} campaignLevels - An array of campaign level objects.
     * @param {object} gameOptions - The game options configuration object.
     * @returns {Promise<void>} A promise that resolves when the guide page is set up.
     */
    async setupGuidePage(biomes, baseConfig, masterItemsConfig, campaignLevels, gameOptions) {
        const pageContainer = await this._loadPageContent('guide', 'pages/guide-content.html');
        if (!pageContainer) return;

        if (!pageContainer.dataset.listenerAttached) {
            const backButton = pageContainer.querySelector('#guide-back-to-game-btn');
            backButton.addEventListener('click', () => {
                // Re-apply the actual game's biome when leaving the guide
                this.biomeManager.applyBiome(this.biomeManager.getCurrentBiomeKey());
                this.showPage('game');
            });
            pageContainer.dataset.listenerAttached = 'true';
        }

        const contentContainer = pageContainer.querySelector('#guide-content');
        if (!contentContainer) return;

        // Get the currently active biome key to set the initial state of the guide
        let viewingThemeKey = this.biomeManager.getCurrentBiomeKey();
        // Apply the theme for the current biome so the guide page matches
        this.biomeManager.applyBiome(viewingThemeKey);

        // Get the default path for the current biome, falling back to 'default' if not found
        let viewingPathLength = biomes[viewingThemeKey]?.defaultPathKey || 'default';

        const refresh = () => {
            const availablePathLengths = biomes[viewingThemeKey].upgradePathLinear;
            const gameConfigForGuide = buildGameConfig({ masterItemsConfig, baseConfig, biomesConfig: biomes, biomeKey: viewingThemeKey, pathLength: viewingPathLength, gridSize: 1 });
            
            const flatMasterItems = Object.values(masterItemsConfig).reduce((acc, category) => {
                return { ...acc, ...category };
            }, {});
            
            const featuresHtml = `
                <hr class="my-8 border-gray-600">
                <h2 class="text-2xl font-bold">Game Features & Ideas</h2>
                <div class="space-y-6 mt-4">
                    ${featuresConfig.map(section => `
                        <div>
                            <h3 class="text-xl font-semibold text-orange-400">${this._getStatusIcon(section.status)} ${section.title}</h3>
                            <ul class="list-disc list-inside ml-4 mt-2 text-gray-300 space-y-2">
                                ${section.items.map(item => {
        const subItemsHtml = item.subItems ? `<ul class="list-disc list-inside ml-6 mt-1 text-gray-400">${item.subItems.map(subItem => `<li>${this._getStatusIcon(subItem.status)} ${subItem.text}</li>`).join('')}</ul>` : '';
        return `<li>${this._getStatusIcon(item.status)} ${item.text}${subItemsHtml}</li>`;
    }).join('')}
                            </ul>
                        </div>
                    `).join('')}
                </div>
            `;

            contentContainer.innerHTML = `
                ${templates.getGuideRulesHtml(this.textConfig)}

                <h3>${this.textConfig.guide.biomesTitle}</h3>
                <p class="mb-2 text-gray-400">${this.textConfig.guide.biomesDescription}</p>
                ${templates.getBiomesHtml(biomes, viewingThemeKey)}

                <h3>${this.textConfig.guide.pathLengthTitle}</h3>
                <p class="mb-2 text-gray-400">${this.textConfig.guide.pathLengthDescription}</p>
                ${templates.getPathLengthOptionsHtml(availablePathLengths, viewingPathLength)}

                <p class="mt-4 mb-2 text-gray-400">${this.textConfig.guide.upgradePathDescription}</p>
                ${templates.getGameItemsHtml(gameConfigForGuide)}

                ${(campaignLevels && gameOptions) ? templates.getCampaignProgressionVisualisationHtml(campaignLevels, biomes, gameOptions, flatMasterItems, this.textConfig) : ''}

                ${featuresHtml}
            `;
        };        // Attach event listeners for biome and path length selection if not already attached
        

        if (!contentContainer.dataset.listenerAttached) {
            contentContainer.addEventListener('click', (e) => {
                let needsRefresh = false;
                if (e.target.matches('.biome-selector')) {
                    viewingThemeKey = e.target.dataset.biome;
                    this.biomeManager.applyBiome(viewingThemeKey); // Apply theme for illustration
                    // When a new biome is selected, reset the path length to its default
                    viewingPathLength = biomes[viewingThemeKey]?.defaultPathKey || 'default';
                    needsRefresh = true;
                }
                if (e.target.matches('.path-length-selector')) {
                    viewingPathLength = e.target.dataset.length;
                    needsRefresh = true;
                }
                if (needsRefresh) refresh();
            });
            contentContainer.dataset.listenerAttached = 'true';
        }

        refresh();
    }

    /**
     * Returns a status icon based on the provided status string.
     * @param {string} status - The status ('done', 'in-progress', 'todo').
     * @returns {string} The corresponding emoji icon.
     * @private
     */
    _getStatusIcon(status) {
        switch (status) {
            case 'done': return '✅';
            case 'in-progress': return '🟡';
            case 'todo': return '❌';
            default: return '';
        }
    }

    /**
     * Sets up the settings page, loading content and handling interactions.
     * @returns {Promise<void>} A promise that resolves when the settings page is set up.
     */
    async setupSettingsPage() {
        const container = await this._loadPageContent('settings', 'pages/settings-content.html');
        if (container && !container.dataset.listenerAttached) {
            const backButton = container.querySelector('#settings-back-to-game-btn');
            if (backButton) backButton.addEventListener('click', () => this.showPage('game'));
            container.dataset.listenerAttached = 'true';
        }
    }

    /**
     * Fetches and injects HTML content for a page if it's not already loaded.
     * @param {string} pageKey - The key of the page in the DOM map.
     * @param {string} contentUrl - The URL of the HTML content to fetch.
     * @returns {Promise<HTMLElement|null>} The container element or null on failure.
     * @private
     */
    async _loadPageContent(pageKey, contentUrl) {
        const container = this.dom.pages[pageKey];
        if (!container) {
            console.error(`UI Error: Page container for '${pageKey}' not found!`);
            return null;
        }
        if (container.innerHTML.trim() !== '') {
            return container; // Content already loaded
        }
        try {
            const response = await fetch(contentUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            container.innerHTML = await response.text();
            return container;
        } catch (error) {
            console.error(`Failed to load content for ${pageKey}: ${error.message}`);
            container.innerHTML = '<p class="text-red-500 text-center">Error: Could not load content.</p>';
            return null;
        }
    }

    setupTestCasesPage(tests, runTestCallback, audioConfig, playAudioCallback) {
        const container = this.dom.pages.testCases;
        const achievementContentContainer = container.querySelector('#test-cases-content');
        const audioContentContainer = container.querySelector('#audio-test-cases-content');

        // Setup achievement tests
        if (achievementContentContainer) {
            achievementContentContainer.innerHTML = templates.getTestCasesHtml(tests);
            if (!achievementContentContainer.dataset.listenerAttached) {
                achievementContentContainer.addEventListener('click', async (e) => {
                    if (e.target.matches('.run-test-btn')) {
                        const testId = e.target.dataset.testId;
                        const resultEl = document.getElementById(`result-${testId}`);
                        
                        e.target.disabled = true;
                        e.target.textContent = 'Running...';
                        resultEl.textContent = '...';

                        const result = await runTestCallback(testId);

                        resultEl.textContent = result.message;
                        resultEl.className = result.success
                            ? 'test-result font-bold ml-4 w-48 text-left text-green-400'
                            : 'test-result font-bold ml-4 w-48 text-left text-red-400';

                        e.target.disabled = false;
                        e.target.textContent = 'Run Test';
                    }
                });
                achievementContentContainer.dataset.listenerAttached = 'true';
            }
        }

        // Setup audio tests
        if (audioContentContainer) {
            audioContentContainer.innerHTML = templates.getAudioTestCasesHtml(audioConfig);
            if (!audioContentContainer.dataset.listenerAttached) {
                audioContentContainer.addEventListener('click', (e) => {
                    if (e.target.matches('.play-sound-btn')) {
                        const soundKey = e.target.dataset.soundKey;
                        playAudioCallback(soundKey);
                    }
                });
                audioContentContainer.dataset.listenerAttached = 'true';
            }
        }

        this.showPage('testCases');
    }

    // --- Grid & Cell Rendering ---

    /**
     * Creates the game grid in the DOM.
     * @param {import('../models/Grid.js').Grid} grid - The grid model instance.
     * @param {number} totalGridSize - The TOTAL size of the grid including buffers (e.g., 11 for 11x11).
     * @param {function(Event): void} clickHandler - The event handler for cell clicks.
     * @param {string} gameMode - The current game mode.
     * @param {boolean} isSettlersModeEnabled - True if settlers mode is enabled.
     */
    createGrid(grid, totalGridSize, clickHandler, gameMode, isSettlersModeEnabled) {
        console.log('%c[UIManager.createGrid] Starting grid creation.', 'color: blue; font-weight: bold;', { totalGridSize, clickHandler, gameMode });
        // Before clearing the grid, explicitly remove the old event listener from each cell.
        if (this.currentClickHandler) {
            console.log('%c[UIManager.createGrid] Removing old click handler.', 'color: orange;', this.currentClickHandler);
            const cells = this.dom.grid.children;
            for (let i = 0; i < cells.length; i++) {
                cells[i].removeEventListener('click', this.currentClickHandler);
            }
        }

        this.gridSize = totalGridSize;
        grid.domElement = this.dom.grid; // Assign the DOM element to the grid instance
        grid.domElement.classList.toggle('settlers-mode-active', isSettlersModeEnabled); // Toggle class here, after domElement is set
        this.dom.grid.style.gridTemplateColumns = `repeat(${totalGridSize}, auto)`;
        this.dom.grid.innerHTML = '';

        // Clear all overlays (NPCs, effects, etc.) to ensure a clean state for the new game.
        // This prevents visual artifacts from previous sessions from persisting.
        this.clearAllTemporaryEffects();

        const existingCollector = document.getElementById('collector-container');
        if (existingCollector) {
            existingCollector.remove();
        }

        this.currentClickHandler = clickHandler;
        console.log('%c[UIManager.createGrid] Set new click handler.', 'color: green;', this.currentClickHandler);

        for (let i = 0; i < totalGridSize; i++) {
            for (let j = 0; j < totalGridSize; j++) {
                const cellElement = document.createElement('div');
                cellElement.classList.add('gem', 'gem-empty');
                cellElement.dataset.row = i;
                cellElement.dataset.col = j;
                cellElement.addEventListener('click', clickHandler);
                this.dom.grid.appendChild(cellElement);
            }
        }

        // Toggle visibility of game-mode-specific controls
        const isMiniMerge = gameMode === 'mini-merge';
        this.dom.miniMergeControls.classList.toggle('hidden', !isMiniMerge);
        this.dom.gameGridWrapper.classList.toggle('mini-merge-layout', isMiniMerge); // Keep for cell sizing
        //this.dom.infoPanel.classList.toggle('hidden', isMiniMerge); // Hide main info panel
        //this.dom.gameStatsBox.classList.toggle('hidden', isMiniMerge); // Hide stats panel

        this._collectorNeedsCreation = (gameMode === 'rolling');
    }

    /**
     * Updates the entire game grid's visual display.
     * @param {Array<Array<object|null>>} grid - The 2D array representing the game grid.
     * @param {number} totalGridSize - The total size of the grid, including buffers.
     * @param {object} config - The active game configuration.
     */
    updateGrid(grid, totalGridSize, config) {
        const isMiniMerge = config.isMiniMerge;
        const handSize = 3;
        const handStartCol = isMiniMerge ? Math.floor((totalGridSize - handSize) / 2) : -1;
        const handEndCol = isMiniMerge ? handStartCol + handSize : -1;
        const handRow = isMiniMerge ? Math.floor((totalGridSize - 1) / 2) : -1;

        for (let i = 0; i < totalGridSize; i++) {
            for (let j = 0; j < totalGridSize; j++) {
                // In Mini-Merge mode, we must not allow the main updateGrid function
                // to overwrite the hand cells, which are managed by updateMiniMergeGrid.
                if (isMiniMerge && i === handRow && j >= handStartCol && j < handEndCol) {
                    continue;
                }

                const cellElement = this.dom.grid.children[i * totalGridSize + j];
                const item = grid[i][j];
                this.updateGridCell(cellElement, item, config);
            }
        }
        this.updateTemporaryEffects();
    }

    /**
     * Updates the visual representation of a single grid cell.
     * @param {HTMLElement} cellElement - The DOM element representing the cell.
     * @param {object|null} item - The item currently in the cell, or null if empty.
     * @param {object} config - The active game configuration.
     */
    updateGridCell(cellElement, item, config) {
        let displayContent = '';

        if (item) {
            // Special rendering for settlements to include their name
            if (item.isStationary && item.recipes) {
                let iconContent = '';
                if (typeof item.display === 'object' && item.display.background) {
                    iconContent = `
                        <div class="compound-emoji">
                            <span class="background">${item.display.background}</span>
                            <span class="foreground">${item.display.foreground || ''}</span>
                        </div>`;
                } else {
                    iconContent = item.display || '';
                }
                displayContent = `
                    <div class="settlement-cell-content">
                        <div class="settlement-icon">${iconContent}</div>
                        <div class="settlement-name">${item.name}</div>
                    </div>`;
            } else {
                displayContent = item.display || '';
            }
        }

        cellElement.innerHTML = displayContent;

        // Ensure that clicks/hovers on the content inside the cell (like emojis or images)
        // are passed through to the parent cell div. This fixes cursor and hover style issues.
        // We must check if the firstChild is an Element (nodeType 1), not a Text node (nodeType 3).
        if (cellElement.firstChild && cellElement.firstChild.nodeType === 1) {
            (cellElement.firstChild).style.pointerEvents = 'none';
        }

        // Intelligently manage classes instead of resetting them, which was causing the click issue.
        const isBuffer = item && item.isBuffer;
        const hasDisplay = displayContent !== '';
        const isAnimal = hasDisplay && item?.key && config?.animals?.some(a => a.key === item.key);

        // Determine if the cell should be inactive (for Mini-Merge mode)
        const isMiniMerge = config.isMiniMerge;
        let isInactive = false;
        if (isMiniMerge) {
            const row = parseInt(cellElement.dataset.row, 10);
            const col = parseInt(cellElement.dataset.col, 10);
            const handSize = 3;
            const handStartCol = Math.floor((this.gridSize - handSize) / 2);
            const handEndCol = handStartCol + handSize;
            const handRow = Math.floor((this.gridSize - 1) / 2);
            isInactive = (row !== handRow || col < handStartCol || col >= handEndCol);
        }


        cellElement.className = 'gem'; // Start with the base class.

        cellElement.style.cssText = ''; // Reset ALL inline styles to prevent animation artifacts

        if (isBuffer) {
            cellElement.classList.add('gem-buffer');
            cellElement.textContent = item.display;
            if (config.bufferBackgroundColor) {
                cellElement.style.backgroundColor = config.bufferBackgroundColor;
            }
            return;
        }

        if (isInactive) {
            cellElement.classList.add('gem-inactive');
            return;
        }

        if (hasDisplay) {
            cellElement.classList.add(isAnimal ? 'gem-animal' : 'gem-filled');
            const itemConfig = item; // The item on the grid is the full config object
            if (itemConfig && itemConfig.style) {
                cellElement.style.backgroundColor = itemConfig.style.backgroundColor;
                if (itemConfig.style.border) {
                    cellElement.style.border = itemConfig.style.border;
                }
            }

            // If colorblind mode is enabled, add a pattern class based on the item's tier.
            if (this.preferencesManager.getPreference('enableColorblindMode', false)) {
                // Use the item's tier index to apply a cyclical pattern.
                const tier = item.tierIndex || 0;
                // Remove old patterns before adding a new one
                for (let i = 0; i < 5; i++) {
                    cellElement.classList.remove(`cb-pattern-${i}`);
                }
                cellElement.classList.add(`cb-pattern-${tier % 5}`); // Example: cycles through 5 patterns
            }
        } else {
            cellElement.classList.add('gem-empty');
        }

        // --- Settlement Inventory Rendering ---
        // If the item is a settlement, render its inventory indicators.
        if (item && item.isStationary && item.recipes) {
            this._renderSettlementInventory(cellElement, item);
        }
    }

    /**
     * Renders the import/export inventory indicators for a settlement cell.
     * @param {HTMLElement} cellElement - The DOM element for the settlement.
     * @param {import('../models/Settlement.js').Settlement} settlement - The settlement instance.
     * @private
     */
    _renderSettlementInventory(cellElement, settlement) {
        const importsInStock = Object.entries(settlement.imports || {}).filter(([, qty]) => qty > 0);
        const exportsInStock = Object.entries(settlement.exports || {}).filter(([, qty]) => qty > 0);

        // Get or create the container for the indicators.
        let indicatorsContainer = cellElement.querySelector('.settlement-indicators');
        if (!indicatorsContainer) {
            indicatorsContainer = document.createElement('div');
            indicatorsContainer.className = 'settlement-indicators';
            // Add dedicated stacks for imports and exports
            indicatorsContainer.innerHTML = `
                <div class="indicator-stack indicator-stack-import"></div>
                <div class="indicator-stack indicator-stack-export"></div>
            `;
            cellElement.appendChild(indicatorsContainer);
        }

        // Generate and render HTML for all stocked items.
        const importStack = indicatorsContainer.querySelector('.indicator-stack-import');
        const exportStack = indicatorsContainer.querySelector('.indicator-stack-export');

        importStack.innerHTML = importsInStock
            .map(([key, qty]) => this._getIndicatorHtml('import', qty, this.masterItemsConfig, key))
            .join('');
        exportStack.innerHTML = exportsInStock
            .map(([key, qty]) => this._getIndicatorHtml('export', qty, this.masterItemsConfig, key))
            .join('');

        // Add a class to the cell if it has items to be picked up.
        cellElement.classList.toggle('has-export', exportsInStock.length > 0);

        // Add pause icons if imports or exports are paused
        if (settlement.importsPaused) {
            importStack.innerHTML += `<div class="indicator indicator-pause">⏸️</div>`;
        }
        if (settlement.exportsPaused) {
            exportStack.innerHTML += `<div class="indicator indicator-pause">⏸️</div>`;
        }
    }

    // --- Overlay Rendering (NPCs, Effects) ---

    /**
     * Updates the display of overlay entities (like NPCs) on the grid.
     * @param {Map<number, object>} entities - A Map of entity IDs to entity objects.
     * @param {HTMLElement} overlayElement - The HTML element serving as the overlay container.
     * @param {string} idPrefix - The prefix for the ID of each entity's DOM element.
     * @param {string} elementClass - The CSS class to apply to each entity's DOM element.
     * @param {boolean} [isHuman=false] - True if the entities being processed are human NPCs.
     */
    updateOverlayEntities(entities, overlayElement, idPrefix, elementClass, isHuman = false) {
        // OPTIMIZATION: Cache cell dimensions. Only recalculate if the grid width changes (e.g., on resize).
        if (this.dom.grid.clientWidth !== this.lastGridWidth) {
            const gridCell = this.dom.grid.children[0];
            if (!gridCell) return; // Grid not ready
            this.cachedCellDimensions.width = gridCell.offsetWidth;
            this.cachedCellDimensions.height = gridCell.offsetHeight;
            this.lastGridWidth = this.dom.grid.clientWidth;
        }
        const { width: cellWidth, height: cellHeight } = this.cachedCellDimensions;
        if (cellWidth === 0) return; // Grid not ready

        overlayElement.style.width = `${this.dom.grid.clientWidth}px`;
        overlayElement.style.height = `${this.dom.grid.clientHeight}px`;

        // --- OPTIMIZATION START ---
        if (!this.renderedEntityIdSets) {
            this.renderedEntityIdSets = new Map();
        }
        if (!this.renderedEntityIdSets.has(idPrefix)) {
            this.renderedEntityIdSets.set(idPrefix, new Set());
        }
        
        const previouslyRenderedIds = this.renderedEntityIdSets.get(idPrefix);
        const activeEntityIds = new Set(entities.keys());

        // Find which IDs are no longer on screen and remove their elements
        for (const id of previouslyRenderedIds) {
            if (!activeEntityIds.has(id)) {
                const element = document.getElementById(`${idPrefix}${id}`);
                if (element) {
                    element.remove();
                }
            }
        }
        // --- OPTIMIZATION END ---

        for (const [id, entity] of entities.entries()) {
            let entityElement = document.getElementById(`${idPrefix}${id}`);
            if (!entityElement) {
                entityElement = this.createOverlayElement(id, entity, overlayElement, idPrefix, elementClass);
            }

            // OPTIMIZATION: The innerHTML of an NPC rarely changes. Only update it if the display property has changed.
            // This is now handled by the Character model having a mutable `display` property.
            const currentDisplay = entityElement.dataset.display;
            if (currentDisplay !== entity.display) entityElement.innerHTML = entity.display;

            // Always update accessory in case it has been swapped
            if (isHuman) {
                entityElement.style.removeProperty('--accessory-icon-left');
                entityElement.style.removeProperty('--accessory-icon-right');

                // Hide accessory if the NPC is currently swapping
                if (entity.state.isSwapping) {
                    // Do nothing, leave properties removed
                } else if (entity.inventory.accessory) {
                    const display = entity.inventory.accessory.display || ''; // Gracefully handle null/undefined
                    if (entity.inventory.accessorySide === 'right') {
                        entityElement.style.setProperty('--accessory-icon-right', `'${display}'`);
                    } else {
                        entityElement.style.setProperty('--accessory-icon-left', `'${display}'`);
                    }
                }
            }

            // Handle fade-out for removal
            if (entity.state.isFadingOut) {
                entityElement.classList.add('fading-out');
            } else {
                entityElement.classList.remove('fading-out');
            }

            // Dynamically add/remove classes based on the NPC's current action.
            const currentAction = entity.state.currentAction || ''; // Default to empty string
            const previousAction = entityElement.dataset.currentAction || '';

            if (currentAction !== previousAction) {
                if (previousAction) {
                    entityElement.classList.remove(`npc-action-${previousAction}`);
                }
                if (currentAction) {
                    entityElement.classList.add(`npc-action-${currentAction}`);
                }
                entityElement.dataset.currentAction = currentAction;
            }

            // Add/remove a class for the "tagged" state in spotting mode.
            entityElement.classList.remove('is-tagged-correct', 'is-tagged-incorrect');
            if (entity.state.taggedState === 'correct') {
                entityElement.classList.add('is-tagged-correct');
            } else if (entity.state.taggedState === 'incorrect') {
                entityElement.classList.add('is-tagged-incorrect');
            }

            let facingAngle = this._getEntityFacingAngle(entity);
            // If the NPC is staring at the player, override its direction to face forward.
            if (currentAction === 'stare_at_player') {
                facingAngle = 90; // A 90-degree turn makes them face the camera in this isometric view.
            }

            let scale = 1;
            // In non-settlers mode, make human NPCs smaller to reduce visual clutter.
            if (isHuman && !this.configManager.getSettings().isSettlersModeEnabled) {
                scale = 0.5;
            }

            const x = entity.position.c * cellWidth// - (cellWidth / 2);
            let y = entity.position.r * cellHeight;

            // --- Bobbing Animation ---
            // Add a small vertical "bob" to the y-position as the NPC moves.
            // We use the movement cooldown to drive a sine wave for a smooth up-and-down motion.
            if (entity.movement && entity.movement.cooldown > 0) {
                const bobbleAmount = 4; // The height of the bob in pixels.
                const bobbleSpeed = 2; // How many bobs per move.
                const progress = (10 - entity.movement.cooldown) / 10; // Cooldown goes from 10 down to 0.
                y -= Math.abs(Math.sin(progress * Math.PI * bobbleSpeed) * bobbleAmount);
            }

            const is3dEnabled = this.preferencesManager.getPreference('enable3dEffects', true);
            const rotateX = is3dEnabled ? 'rotateX(-50deg)' : '';
            entityElement.style.transform = `translate3d(${x}px, ${y}px, 1px) ${rotateX} rotateY(${facingAngle}deg) scale(${scale})`;
        }

        // After the loop, update the set of rendered IDs for the next frame.
        this.renderedEntityIdSets.set(idPrefix, activeEntityIds);

        // If there's an active tooltip, update its position to follow the NPC
        if (this.activeNpcTooltip) {
            this._updateNpcTooltipPosition();
        }
    }

    /**
     * Determines the Y-axis rotation angle for an entity based on its direction.
     * @param {object} entity - The entity object.
     * @returns {number} The rotation angle in degrees (0 or 180).
     * @private
     */
    _getEntityFacingAngle(entity) {
        const baseDirection = entity.config.characterFacingDirection || 'left';
        const travellingDirection = entity.movement.direction || 'left';

        if (baseDirection === 'right') {
            return travellingDirection === 'left' ? 180 : 0;
        }
        // Default base direction is left
        return travellingDirection === 'right' ? 180 : 0;
    }

    /**
     * Creates a new DOM element for an overlay entity.
     * @param {number} id - The unique ID of the entity.
     * @param {object} entity - The entity object.
     * @param {HTMLElement} overlayElement - The parent overlay element.
     * @param {string} idPrefix - The prefix for the element's ID.
     * @param {string} elementClass - The CSS class for the element.
     * @returns {HTMLElement} The newly created DOM element.
     */
    createOverlayElement(id, entity, overlayElement, idPrefix, elementClass) {
        const entityElement = document.createElement('div');
        entityElement.id = `${idPrefix}${id}`;
        entityElement.className = elementClass;
        entityElement.innerHTML = entity.display; // Use the mutable display property
        entityElement.dataset.display = entity.display; // Store the current display for quick comparison
        // Only make NPCs interactive (hoverable for tooltips) in Settlers Mode.
        // In other modes, they should be purely visual and not block grid clicks.
        if (this.configManager.getSettings().isSettlersModeEnabled) {
            entityElement.style.pointerEvents = 'auto';
        }

        if (entity.inventory.accessory) {
            if (entity.inventory.accessorySide === 'right') {
                entityElement.style.setProperty('--accessory-icon-right', `'${entity.inventory.accessory.display}'`);
            } else {
                entityElement.style.setProperty('--accessory-icon-left', `'${entity.inventory.accessory.display}'`);
            }
        }
        entityElement.title = `${entity.config.name}: ${entity.config.description}`;
        overlayElement.appendChild(entityElement);
        return entityElement;
    }

    /**
     * Adds a temporary visual effect to a specific grid cell.
     * The effect is added to a collection and will be rendered and managed by the update loop.
     * @param {number} row - The row of the cell.
     * @param {number} col - The column of the cell.
     * @param {object|string} effectConfig - The configuration object for the effect, or a string key to look up in temporaryEffectsConfig.
     * @returns {string|null} The unique ID of the created effect, or null if creation failed.
     */
    addTemporaryEffect(row, col, effectConfig) {
        let config = effectConfig;
        if (typeof config === 'string') {
            config = temporaryEffectsConfig[config];
        }

        if (!config || typeof config.display === 'undefined') {
            console.warn('Invalid effectConfig provided to addTemporaryEffect:', effectConfig);
            return null;
        }

        // Handle array of display options by picking one randomly
        let displayContent = config.display;
        if (Array.isArray(displayContent)) {
            displayContent = displayContent[Math.floor(Math.random() * displayContent.length)];
        }

        const startTime = Date.now() + (config.delay || 0);
        const effectId = `temp-effect-${config.className || 'default'}-${row}-${col}-${Date.now()}-${Math.random()}`;
        // Store the chosen display content, not the whole array
        this.temporaryEffects.set(effectId, { ...config, display: displayContent, row, col, startTime, id: effectId, fading: false });

        // If there's a delay, we don't need to render immediately,
        // but we'll trigger an update to ensure the lifecycle check runs.
        // If no delay, update immediately.
        if (!config.delay) {
            this.updateTemporaryEffects();
        }

        return effectId;
    }

    /**
     * Clears a temporary visual effect by its unique ID.
     * @param {string} effectId - The ID of the effect to remove.
     */
    clearTemporaryEffectById(effectId) {
        if (this.temporaryEffects.has(effectId)) {
            // Immediately remove the element from the DOM.
            const effectElement = document.getElementById(effectId);
            if (effectElement) {
                effectElement.remove();
            }
            this.temporaryEffects.delete(effectId);
        }
    }

    /**
     * Clears all temporary effects from both the data map and the DOM.
     */
    clearAllTemporaryEffects() {
        this.temporaryEffects.clear();
        this.dom.temporaryEffectsOverlay.innerHTML = '';
        this.dom.npcOverlay.innerHTML = '';
        this.dom.nonHumanNPCOverlay.innerHTML = '';
    }
    /**
     * Renders static obstacles like walls on their own overlay.
     * @param {Set<string>} obstacles - A set of "r,c" strings for obstacle locations.
     */
    renderObstacles(obstacles) {
        // If the game page is not visible, the grid won't have dimensions.
        // Defer rendering until the page is shown.
        if (this.dom.pages.game.classList.contains('hidden')) {
            this._obstaclesNeedRendering = true;
            this._obstaclesToRender = obstacles; // Store the data
            return;
        }

        const overlay = this.dom.overlays.obstacleOverlay;
        if (!overlay) return;

        overlay.innerHTML = ''; // Clear previous obstacles

        const gridCell = this.dom.grid.children[0];
        if (!gridCell || gridCell.offsetWidth === 0) return; // Grid not ready or not rendered
        const cellWidth = gridCell.offsetWidth;
        const cellHeight = gridCell.offsetHeight;

        overlay.style.width = `${this.dom.grid.clientWidth}px`;
        overlay.style.height = `${this.dom.grid.clientHeight}px`;

        obstacles.forEach(obstacleKey => {
            const parts = obstacleKey.split('-');
            const type = parts[0];
            const posKey = parts.pop();
            const [r, c] = posKey.split(',').map(Number);

            if (type === 'h' || type === 'v') {
                const wallElement = document.createElement('div');
                wallElement.className = `obstacle-wall obstacle-wall-${type}`;
                wallElement.title = obstacleKey; // Add the key for debugging
                if (type === 'h') {
                    wallElement.style.left = `${(c * cellWidth) + 10}px`;
                    wallElement.style.top = `${(r + 1) * cellHeight - 14}px`;
                    wallElement.style.width = `${cellWidth - 14}px`;
                    wallElement.style.height = `${cellHeight * 0.2}px`;
                } else {
                    wallElement.style.left = `${(c + 1) * cellWidth - 4}px`;
                    wallElement.style.top = `${((r) * cellHeight) + 10}px`;
                    wallElement.style.width = `${cellWidth * 0.2}px`;
                    wallElement.style.height = `${cellHeight - 14}px`;
                }
                overlay.appendChild(wallElement);
            } else if (type === 'furniture') {
                const itemKey = parts.slice(1).join('-');
                const furnitureConfig = this.masterItemsConfig.streetFurniture[itemKey];
                if (furnitureConfig) {
                    const furnitureElement = document.createElement('div');
                    furnitureElement.className = 'obstacle-furniture';
                    furnitureElement.title = obstacleKey; // Add the key for debugging
                    furnitureElement.innerHTML = furnitureConfig.display;
                    furnitureElement.style.left = `${(c) * cellWidth}px`;
                    furnitureElement.style.top = `${((r) * cellHeight + (cellHeight/2) - 4)}px`; // Center on the line
                    furnitureElement.style.width = `${cellWidth}px`;
                    overlay.appendChild(furnitureElement);
                }
            }
        });
    }
    /**
     * Renders roads on their own overlay.
     * @param {Set<string>} roadTiles - A set of "r,c" strings for road tile locations.
     */
    renderRoads(roadTiles) {
        // If the game page is not visible, the grid won't have dimensions.
        // Defer rendering until the page is shown.
        if (this.dom.pages.game.classList.contains('hidden')) {
            this._roadsNeedRendering = true;
            this._roadsToRender = roadTiles; // Store the data
            return;
        }

        const overlay = this.dom.overlays.roadOverlay;
        if (!overlay) return;

        overlay.innerHTML = ''; // Clear previous roads

        const gridCell = this.dom.grid.children[0];
        if (!gridCell || gridCell.offsetWidth === 0) return; // Grid not ready or not rendered
        const cellWidth = gridCell.offsetWidth;
        const cellHeight = gridCell.offsetHeight;

        overlay.style.width = `${this.dom.grid.clientWidth}px`;
        overlay.style.height = `${this.dom.grid.clientHeight}px`;

        let counter = 0;
        roadTiles.forEach(roadKey => {
            const [orientation, posKey] = roadKey.split('-');
            const [r, c] = posKey.split(',').map(Number);
            const roadElement = document.createElement('div');
            roadElement.className = `road-tile road-tile-${orientation}`;

            if (orientation === 'h') { // Horizontal road on the bottom edge of cell (r, c)
                roadElement.style.left = `${c * cellWidth}px`;
                roadElement.style.top = `${(r + 1) * cellHeight - 1}px`; // Center on the grid line
                roadElement.style.width = `${cellWidth}px`;
                roadElement.style.height = '11px'; // Skinny road line
            } else { // Vertical road on the right edge of cell (r, c)
                roadElement.style.left = `${(c + 1) * cellWidth - 1}px`; // Center on the grid line
                roadElement.style.top = `${r * cellHeight}px`;
                roadElement.style.width = '11px'; // Skinny road line
                roadElement.style.height = `${cellHeight}px`;
            }
            // Add a counter for debugging the draw order.
            //roadElement.textContent = counter++;
            overlay.appendChild(roadElement);
        });
    }


    /**
     * Clears all visual effects from the disaster overlay.
     */
    clearDisasterEffects() {
        this.dom.disasterOverlay.innerHTML = '';
    }

    /**
     * Updates the display of temporary effects on the grid overlay.
     */
    updateTemporaryEffects() {
        const overlayElement = this.dom.temporaryEffectsOverlay;
        const gridCell = this.dom.grid.children[0];
        if (!gridCell) return; // Grid not ready

        const cellWidth = gridCell.offsetWidth;
        const cellHeight = gridCell.offsetHeight;
        overlayElement.style.width = `${this.dom.grid.clientWidth}px`;
        overlayElement.style.height = `${this.dom.grid.parentNode.clientHeight}px`;

        // This function now only handles rendering/updating, not removal.
        // Removal is handled in updateTemporaryEffectsLifecycle.

        // Update or create elements for active effects
        for (const [id, effect] of this.temporaryEffects.entries()) {
            if (effect.startTime > Date.now()) continue;

            let effectElement = document.getElementById(id);

            if (!effectElement) {
                effectElement = document.createElement('div');
                effectElement.id = id;
                effectElement.classList.add('temporary-effect');
                if (effect.className) {
                    effectElement.classList.add(effect.className);
                }
                overlayElement.appendChild(effectElement);
            }

            // --- Apply styles and content ---
            effectElement.innerHTML = effect.display;
            effectElement.style.position = 'absolute';
            effectElement.style.left = `${effect.col * cellWidth}px`;
            effectElement.style.top = `${effect.row * cellHeight}px`;
            effectElement.style.width = `${cellWidth}px`;
            effectElement.style.height = `${cellHeight}px`;
            effectElement.style.display = 'flex';
            effectElement.style.justifyContent = 'center';
            effectElement.style.alignItems = 'flex-end';
            effectElement.style.fontSize = '2em';
            effectElement.style.pointerEvents = 'none';

            if (effect.backgroundColor) {
                effectElement.style.backgroundColor = effect.backgroundColor;
            }

            // --- Handle Fading ---
            if (effect.fading) {
                const fadeDuration = (effect.duration || 500) / 1000; // Convert to seconds
                effectElement.style.transition = `opacity ${fadeDuration}s ease-out`;
                effectElement.style.opacity = '0';
            } else {
                effectElement.style.transition = 'opacity 0.2s ease-in';
                effectElement.style.opacity = '1';
            }
        }
    }

    /**
     * Creates and animates two accessory icons swapping positions between two NPCs.
     * @param {object} npc1 - The first NPC object.
     * @param {object} npc2 - The second NPC object.
     */
    animateAccessorySwap(npc1, npc2) {
        const overlay = this.dom.temporaryEffectsOverlay;
        const gridCell = this.dom.grid.children[0];
        if (!gridCell) return;

        const cellWidth = gridCell.offsetWidth;
        const cellHeight = gridCell.offsetHeight;

        // Get the starting pixel positions for each accessory, centered on their current owner.
        const pos1 = {
            x: npc1.position.c * cellWidth - cellWidth / 2,
            y: npc1.position.r * cellHeight + cellHeight / 2
        };
        const pos2 = {
            x: npc2.position.c * cellWidth - cellWidth / 2,
            y: npc2.position.r * cellHeight + cellHeight / 2
        };

        // Calculate the landing position for each accessory, offsetting it to the correct hand of the receiving NPC.
        const handOffset = cellWidth * 0.25; // Move it 25% of a cell width to the side.

        // Determine if an NPC is visually flipped.
        const isFlipped = (npc) => {
            const baseDirection = npc.config.characterFacingDirection || 'left';
            const travellingDirection = npc.movement.direction || 'left';
            return baseDirection !== travellingDirection;
        };

        // The accessory will land on the side of the NPC it's coming from.
        // We need to adjust this based on the receiving NPC's orientation.
        const sideForNpc1 = isFlipped(npc1) ? (npc2.inventory.accessorySide === 'left' ? 'right' : 'left') : npc2.inventory.accessorySide;
        const sideForNpc2 = isFlipped(npc2) ? (npc1.inventory.accessorySide === 'left' ? 'right' : 'left') : npc1.inventory.accessorySide;

        const landingPos1 = { ...pos1, x: pos1.x + (sideForNpc1 === 'left' ? -handOffset : handOffset) };
        const landingPos2 = { ...pos2, x: pos2.x + (sideForNpc2 === 'left' ? -handOffset : handOffset) };

        // Create animator for NPC1's accessory moving to NPC2
        const anim1 = this._createAccessoryAnimator(npc1.inventory.accessory, pos1, npc1.inventory.accessorySide);
        overlay.appendChild(anim1);

        // Create animator for NPC2's accessory moving to NPC1
        const anim2 = this._createAccessoryAnimator(npc2.inventory.accessory, pos2, npc2.inventory.accessorySide);
        overlay.appendChild(anim2);

        // Force reflow to apply initial styles before transition
        void anim1.offsetWidth;
        void anim2.offsetWidth;

        // Apply final styles to trigger transition.
        // NPC1's accessory (anim1) moves to NPC2's hand (landingPos2).
        // NPC2's accessory (anim2) moves to NPC1's hand (landingPos1).
        anim1.style.transform = `translate3d(${landingPos2.x}px, ${landingPos2.y}px, 0px) scale(1) rotate(360deg)`;
        anim2.style.transform = `translate3d(${landingPos1.x}px, ${landingPos1.y}px, 0px) scale(1) rotate(360deg)`;

        // Clean up elements after animation
        setTimeout(() => {
            anim1.remove();
            anim2.remove();
        }, 1000); // Match animation duration
    }


    /**
     * Creates a visual effect for an item falling off the bottom of the grid.
     * The item floats down and fades away.
     * @param {object} item - The item object that is falling.
     * @param {number} row - The starting row of the item.
     * @param {number} col - The starting column of the item.
     */
    createFallingItemEffect(item, row, col) {
        // This effect is a fire-and-forget animation. We create a config object
        // on the fly and pass it to the main temporary effect handler.
        const effectConfig = {
            display: item.display,
            duration: 3000, // The animation duration
            className: 'falling-item-effect',
            // Pass through the item's specific styles
            style: item.style
        };

        // Use the existing system to manage the effect's lifecycle.
        // This ensures it's rendered correctly and cleaned up automatically.
        this.addTemporaryEffect(row, col, effectConfig);
    }



    /**
     * Helper to create a single DOM element for an accessory animation.
     * @param {object} accessory - The accessory item object.
     * @param {{x: number, y: number}} startPos - The starting pixel position.
     * @returns {HTMLElement} The created DOM element.
     * @param {string} side - The side ('left' or 'right') the accessory starts on.
     * @private
     */
    _createAccessoryAnimator(accessory, startPos, side) {
        const animator = document.createElement('div');
        animator.className = 'accessory-animator'; // A new class for styling
        animator.innerHTML = accessory.display || '';
        animator.style.left = '0px';
        animator.style.top = '0px';
        const handOffset = 10; // A small pixel offset to start from the hand, not the center.
        const initialX = startPos.x + (side === 'left' ? -handOffset : handOffset);
        animator.style.transform = `translate3d(${initialX}px, ${startPos.y}px, 50px) scale(1)`;
        return animator;
    }

    // --- Animations ---

    /**
     * Animates the merging of items on the grid.
     * @param {Set<string>} group - A Set of string-formatted coordinates ('r,c') of the items being merged.
     * @param {{r: number, c: number}} newItemCoords - The coordinates where the new item will appear.
     * @param {object|null} newItemObject - The new item object that results from the merge, or null if items are cleared.
     * @param {object} config - The active game configuration.
     * @param {function(): void} onComplete - Callback function to execute after the animation completes.
     */
    animateMerge(group, newItemCoords, newItemObject, config, onComplete) {
        const gridSize = Math.sqrt(this.dom.grid.children.length);
        const gridCell = this.dom.grid.children[0];
        if (!gridCell) { onComplete(); return; }

        const cellWidth = gridCell.offsetWidth;
        const cellHeight = gridCell.offsetHeight;

        group.forEach(pos => {
            const [r, c] = pos.split(',').map(Number);
            const cellElement = this.dom.grid.children[r * gridSize + c];
            cellElement.style.position = 'relative';
            cellElement.style.zIndex = '5';
            const deltaX = (newItemCoords.c - c) * cellWidth;
            const deltaY = (newItemCoords.r - r) * cellHeight;
            cellElement.style.transition = 'transform 0.3s ease-in, opacity 0.3s ease-in, background-color 0.3s ease-in';
            void cellElement.offsetWidth; // Force reflow
            cellElement.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(0)`;
            cellElement.style.opacity = '0';
            cellElement.style.backgroundColor = 'transparent';
        });

        setTimeout(() => {
            // First, ensure all old cells are completely cleared and hidden
            // before rendering the new item. This prevents the flicker.
            group.forEach(pos => {
                const [r, c] = pos.split(',').map(Number);
                const cellElement = this.dom.grid.children[r * gridSize + c];
                cellElement.style.cssText = ''; // Reset all inline styles
                cellElement.className = 'gem gem-empty'; // Set to empty state
                cellElement.textContent = '';
            });

            if (newItemObject) {
                const newCellElement = this.dom.grid.children[newItemCoords.r * gridSize + newItemCoords.c];
                newCellElement.style.zIndex = '10';
                this.updateGridCell(newCellElement, newItemObject, config);
                newCellElement.classList.add('growing-in');
                this.playAudio('merge', { x: newItemCoords.c, gridWidth: gridSize });

                setTimeout(() => {
                    newCellElement.style.zIndex = '';
                    newCellElement.classList.remove('growing-in');
                    onComplete();
                }, 300);
            } else {
                // If no new item, just complete the process
                onComplete();
            }
        }, 300);
    }

    // --- Info & HUD Updates ---

    updateScore(score) { this.dom.score.textContent = score; }
    updateGold(amount) { this.dom.gold.textContent = amount; }
    /**
     * Updates the turn counter display.
     * @param {number} turns - The current turn count (either turns taken or turns remaining).
     * @param {number} [turnLimit] - Optional. The total turn limit for the mode.
     */
    updateNumberOfTurns(turns, turnLimit) {
        const turnText = turnLimit ? `${turns} / ${turnLimit}` : turns;
        this.dom.numberOfTurns.textContent = turnText;
    }
    updateGameModeDisplay(text) { this.dom.gameModeDisplay.textContent = text; }
    updateLevelDetailsDisplay(text) { this.dom.levelDetailsDisplay.textContent = text; }
    toggleSaveButton(enabled) { this.dom.buttons.saveGame.style.display = enabled ? 'block' : 'none'; }

 /**
     * Toggles the visibility of the Nudge button for Mini Merge mode.
     * @param {boolean} enabled - True to show the button, false to hide it.
     */
    toggleNudgeButton(enabled) {
        const nudgeButton = this.dom.miniMergeControls.querySelector('#nudge-mini-merge-btn');
        if (nudgeButton) {
            nudgeButton.disabled = !enabled;
            nudgeButton.classList.toggle('nudge-active', enabled);
            nudgeButton.classList.toggle('nudge-inactive', !enabled);
        }
    }

 /**
     * Updates the text content of the mute toggle button based on the current mute state.
     * @param {boolean} isMuted - The current mute state.
     */
    updateMuteButtonText(isMuted) {
        this.dom.buttons.muteToggle.textContent = isMuted ? 'Unmute all' : 'Mute all';
    }

    updateTargetScore(target) {
        const display = this.dom.targetScoreDisplay;
        const valueSpan = this.dom.targetScoreValue;
        if (target) {
            valueSpan.textContent = target;
            display.classList.remove('hidden');
        } else {
            display.classList.add('hidden');
        }
    }




    /**
     * Updates the item queue display.
     * @param {Array<object>} queue - The array of item objects in the queue.
     * @param {number} turnsUntilAnimal - The number of turns until the next animal spawns. -1 if not applicable.
     * @param {object|null} nextAnimal - The configuration object for the next animal to spawn.
     * @param {string} [gameMode] - The current game mode, to apply special logic.
     * @param {number} [selectedQueueIndex=-1] - The index of the selected item in Mini Merge mode.
     */
    updateQueue(queue, turnsUntilAnimal, nextAnimal, gameMode, selectedQueueIndex = -1) {
        const isMiniMerge = gameMode === 'mini-merge';

        if (gameMode === 'rolling' && !isMiniMerge) {
            this.dom.gemQueueBox.classList.add('hidden');
            return;
        }
        this.dom.gemQueueBox.classList.remove('hidden');
        this.dom.queue.innerHTML = '';
        queue.forEach((itemObject, index) => {
            const queueItem = document.createElement('div');
            queueItem.classList.add('queue-gem');

            if (itemObject) { // Check if the item exists before trying to render it
                queueItem.innerHTML = itemObject.display;
                if (itemObject.style) {
                    queueItem.style.backgroundColor = itemObject.style.backgroundColor;
                    if (itemObject.style.border) {
                        queueItem.style.border = itemObject.style.border;
                    }
                }
                if (isMiniMerge) {
                    queueItem.dataset.index = index;
                    queueItem.classList.add('cursor-pointer'); // Make it look clickable
                    queueItem.classList.toggle('mini-merge-selected', index === selectedQueueIndex);
                }
                if (itemObject.isPowerUp) {
                    queueItem.classList.add('power-up-glow');
                }
                if (index === 0 && !isMiniMerge) {
                    queueItem.classList.add('next-item-highlight');
                    this.dom.grid.classList.toggle('power-up-active', itemObject.isPowerUp);
                }
            } else {
                // Style for an empty slot in the queue
                queueItem.style.backgroundColor = '#374151'; // A neutral, empty color
            }

            // If an animal is spawning on this turn, add an indicator
            if (nextAnimal && turnsUntilAnimal > 0 && (index + 1) === turnsUntilAnimal) {
                queueItem.classList.add('animal-spawn-indicator');
                queueItem.innerHTML += `<span class="animal-spawn-icon">${nextAnimal.display}</span>`;
            }
            this.dom.queue.appendChild(queueItem);
        });
    }

    displayMessage(msg) {
        this.dom.messageBox.innerHTML = `<p>${msg}</p>`;
    }

    // --- Component & Content Rendering ---

    /**
     * Displays the high scores in the info panel.
     * @param {Array<{score: number, initials: string}>} scores - The array of high score objects.
     */
    displayHighScores(scores) {
        const listElement = this.dom.highScoresList;
        listElement.innerHTML = templates.getHighScoresHtml(scores, this.textConfig);
    }

    // --- Settings & Configuration Displays ---

    displayQuickGameSettings(biomes, availableGridSizes, currentSettings) {
        const pageContainer = this.dom.pages.quickGameSettings;
        if (!pageContainer) return;

        let contentContainer = pageContainer.querySelector('#quick-game-settings-content');
        if (!contentContainer) return;

        this.quickGameSelectedSettings = { ...currentSettings };

        const refresh = () => {
            contentContainer.innerHTML = `
                ${templates.getGameModeHtml(this.quickGameSelectedSettings.gameMode, ['linear', 'crafting', 'rolling'])}
                ${templates.getGridSizeHtml(availableGridSizes, this.quickGameSelectedSettings.gridSize)}
                <h3>${this.textConfig.guide.biomesTitle}</h3>
                ${templates.getBiomesHtml(biomes, this.quickGameSelectedSettings.biomeKey)}
                <h3>${this.textConfig.guide.pathLengthTitle}</h3>
                ${this.quickGameSelectedSettings.gameMode === 'crafting' ? '<p class="text-gray-400">Path length is pre-defined in Crafting Mode.</p>' : templates.getPathLengthOptionsHtml(biomes[this.quickGameSelectedSettings.biomeKey].upgradePathLinear, this.quickGameSelectedSettings.pathLength)}
                ${templates.getLimitedTurnsToggleHtml(this.quickGameSelectedSettings.isLimitedTurnsMode, this.gameOptions.defaultTurnLimit)}
                <hr class="my-4 border-gray-600">
                ${templates.getEnableAnimalsToggleHtml(this.quickGameSelectedSettings.isAnimalsEnabled)}
                ${templates.getDisastersToggleHtml(this.quickGameSelectedSettings.isDisastersEnabled)}
                ${templates.getRandomQueueToggleHtml(this.quickGameSelectedSettings.isRandomQueueEnabled)}
                ${templates.getStationaryObjectsToggleHtml(this.quickGameSelectedSettings.isStationaryObjectsEnabled)}
                ${templates.getSettlersModeToggleHtml(this.quickGameSelectedSettings.isSettlersModeEnabled)}
                ${templates.getPowerupsToggleHtml(this.quickGameSelectedSettings.isPowerupsEnabled)}
            `;
        };

        // Use a single, delegated event listener for the whole settings container.
        if (!contentContainer.dataset.listenerAttached) {
            contentContainer.addEventListener('click', (e) => {
                let needsRefresh = false;
                const button = e.target.closest('button');
                if (button) {
                    if (button.dataset.mode) {
                        this.quickGameSelectedSettings.gameMode = button.dataset.mode;
                        needsRefresh = true;
                    } else if (button.dataset.size) {
                        this.quickGameSelectedSettings.gridSize = parseInt(button.dataset.size, 10);
                        needsRefresh = true;
                    } else if (button.dataset.biome) {
                        this.quickGameSelectedSettings.biomeKey = button.dataset.biome;
                        this.biomeManager.applyBiome(this.quickGameSelectedSettings.biomeKey);
                        this.quickGameSelectedSettings.pathLength = biomes[this.quickGameSelectedSettings.biomeKey].defaultPathKey;
                        needsRefresh = true;
                    } else if (button.dataset.length) {
                        this.quickGameSelectedSettings.pathLength = button.dataset.length;
                        needsRefresh = true;
                    }
                }

                if (needsRefresh) refresh();
            });

            contentContainer.addEventListener('change', (e) => {
                const toggleIdMap = {
                    'limited-turns-toggle': 'isLimitedTurnsMode',
                    'disable-animals-toggle': 'isAnimalsEnabled',
                    'disasters-toggle': 'isDisastersEnabled',
                    'random-queue-toggle': 'isRandomQueueEnabled',
                    'stationary-objects-toggle': 'isStationaryObjectsEnabled',
                    'powerups-toggle': 'isPowerupsEnabled',
                    'settlers-mode-toggle': 'isSettlersModeEnabled'
                };
                const key = toggleIdMap[e.target.id];
                if (key) {
                    this.quickGameSelectedSettings[key] = e.target.checked;
                }
            });
            contentContainer.dataset.listenerAttached = 'true';
        }

        refresh();
    }

    /**
     * Displays the settings for starting a new campaign.
     * @param {object} currentSettings - The default settings for the campaign.
     */
    displayCampaignSettings(currentSettings) {
        const pageContainer = this.dom.pages.campaignSettings;
        if (!pageContainer) return;

        let contentContainer = pageContainer.querySelector('#campaign-settings-content');
        if (!contentContainer) return;

        this.campaignSelectedSettings = { ...currentSettings };

        const refresh = () => {
            contentContainer.innerHTML = `
                ${templates.getGameModeHtml(this.campaignSelectedSettings.gameMode)}
                <hr class="my-4 border-gray-600">
            `;
        };

        if (!contentContainer.dataset.listenerAttached) {
            contentContainer.addEventListener('click', (e) => {
                const button = e.target.closest('button');
                if (button && button.dataset.mode) {
                    this.campaignSelectedSettings.gameMode = button.dataset.mode;
                    refresh();
                }
            });
            contentContainer.dataset.listenerAttached = 'true';
        }

        refresh();
    }

    /**
     * Populates the settings page with save game information and controls.
     */
    populateSettingsPage() {
        const settingsContainer = this.dom.pages.settings;
        const saveSlots = this.saveManager.getAllSaveSlots();
        const playerStats = this.playerStatsManager.stats;
        this.displaySettingsPage(
            saveSlots,
            playerStats,
            (slotId) => {
                const savedSlot = this.saveManager.getSaveSlotById(slotId);
                this.eventEmitter.emit('ui:loadGame', { type: savedSlot.type, level: savedSlot.level }); // Emitting event
            },
            (slotId) => {
                const savedSlot = this.saveManager.getSaveSlotById(slotId);
                this.eventEmitter.emit('ui:deleteGame', { type: savedSlot.type, level: savedSlot.level }); // Emitting event
                this.populateSettingsPage(); // Repopulate after deletion
            }
        );

        // Call the new method to display preferences
        this.displayPreferencesSettings(this.preferencesManager);

        // Use a delegated listener on the container if it doesn't have one yet
        const contentArea = settingsContainer.querySelector('#settings-content-area');
        if (contentArea && !contentArea.dataset.resetListener) {
            contentArea.addEventListener('click', (e) => {
                if (e.target.id === 'reset-profile-btn') {
                    this.showPrompt('resetProfileConfirm');
                }
            });
            contentArea.dataset.resetListener = 'true';
        }
    }

    displaySettingsPage(saveSlots, playerStats, onLoad, onDelete) {
        const container = this.dom.pages.settings.querySelector('#settings-content-area');
        if (!container) return;

        container.innerHTML = templates.getSettingsPageHtml(saveSlots, playerStats);

        if (!container.dataset.listenerAttached) {
            container.addEventListener('click', (e) => {
                const button = e.target.closest('button');
                if (!button) return;

                const slotId = button.dataset.slotId;
                if (button.classList.contains('load-game-btn')) {
                    onLoad(slotId);
                } else if (button.classList.contains('delete-game-btn')) {
                    onDelete(slotId);
                }
            });
            container.dataset.listenerAttached = 'true';
        }
    }

    /**
     * Displays the player preferences settings.
     * @param {import('./PreferencesManager.js').PreferencesManager} preferencesManager - The PreferencesManager instance.
     */
    displayPreferencesSettings(preferencesManager) {
        const preferencesContainer = this.dom.pages.settings.querySelector('#preferences-content');
        if (!preferencesContainer) return;

        // Get current preference state
        const isNpcWanderingEnabled = preferencesManager.getPreference('isNpcWanderingEnabled', true); // Default to true
        const enableWeatherEffects = preferencesManager.getPreference('enableWeatherEffects', true); // Default to true
        const enableAmbientAnimals = preferencesManager.getPreference('enableAmbientAnimals', true); // Default to true
        const enable3dEffects = preferencesManager.getPreference('enable3dEffects', true); // Default to true
        const enableColorblindMode = preferencesManager.getPreference('enableColorblindMode', false); // Default to false

        // Generate HTML for the toggles
        preferencesContainer.innerHTML = `
            ${getNpcWanderingToggleHtml(isNpcWanderingEnabled)}
            ${getWeatherEffectsToggleHtml(enableWeatherEffects)}
            ${templates.getAmbientAnimalsToggleHtml(enableAmbientAnimals)}
            ${templates.get3dEffectsToggleHtml(enable3dEffects)}
            ${templates.getColorblindModeToggleHtml(enableColorblindMode)}
        `;

        if (!preferencesContainer.dataset.listenerAttached) {
            preferencesContainer.addEventListener('change', (e) => {
                const toggleIdMap = {
                    'npc-wandering-toggle': 'isNpcWanderingEnabled',
                    'weather-effects-toggle': 'enableWeatherEffects',
                    'ambient-animals-toggle': 'enableAmbientAnimals',
                    'd3-effects-toggle': 'enable3dEffects',
                    'colorblind-mode-toggle': 'enableColorblindMode'
                };
                const key = toggleIdMap[e.target.id];
                if (key) {
                    const isEnabled = e.target.checked;
                    preferencesManager.setPreference(key, isEnabled);
                    if (key === 'enable3dEffects') {
                        this._apply3dEffectsPreference();
                    }
                }
            });
            preferencesContainer.dataset.listenerAttached = 'true';
        }
    }

    /**
     * Applies or removes 3D-related CSS classes based on user preference.
     * @private
     */
    _apply3dEffectsPreference() {
        const isEnabled = this.preferencesManager.getPreference('enable3dEffects', true);
        const elementsToToggle = [
            document.getElementById('game-container'),
            document.getElementById('game-grid-wrapper'),
            this.dom.grid,
            this.dom.npcOverlay,
            this.dom.nonHumanNPCOverlay,
            this.dom.temporaryEffectsOverlay,
            this.dom.ambientAnimalOverlay,
            this.dom.weatherOverlay,
            this.dom.overlays.obstacleOverlay
        ];

        elementsToToggle.forEach(el => {
            if (el) el.classList.toggle(el.id + '-3d', isEnabled);
        });
    }

    // --- Prompts & Modals ---

    /**
     * Displays the level complete page and sets up the "Next Level" button.
     * @param {function(): void} onNextLevel - The callback function to execute when the "Next Level" button is clicked.
     */
    showLevelComplete(onNextLevel) {
        const pageContainer = this.dom.pages.levelComplete;
        const panelContainer = pageContainer.querySelector('.panel-container');
        if (!panelContainer) return;

        panelContainer.innerHTML = templates.getLevelCompleteHtml();
        this.showPage('levelComplete');

        const nextLevelButton = panelContainer.querySelector('#next-level-btn');
        nextLevelButton.addEventListener('click', onNextLevel, { once: true });
    }

    promptForInitials(score, onSave) {
        this.dom.messageBox.innerHTML = templates.getInitialsPromptHtml(score, this.textConfig);

        const input = document.getElementById('initials-input');
        const saveButton = document.getElementById('save-score-button');
        input.focus();
        const saveHandler = () => {
            const initials = input.value.toUpperCase() || '???';
            onSave(initials);
        };
        saveButton.addEventListener('click', saveHandler);
        input.addEventListener('keydown', (e) => { if (e.key === 'Enter') saveHandler(); });
    }

    showPrompt(promptKey, detail = {}) {

       const allPrompts = getPromptsConfig(detail);
        const promptConfig = allPrompts[promptKey];        if (!promptConfig) {
            console.error(`Unknown prompt key: ${promptKey}`);
            return;
        }
        const targetPageKey = promptConfig.targetPage || 'prompt';
        const pageContainer = this.dom.pages[targetPageKey];

        // Convert camelCase page key to kebab-case for matching HTML IDs
        const idPrefix = targetPageKey.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);

        pageContainer.querySelector(`#${idPrefix}-title`).textContent = promptConfig.title;
        pageContainer.querySelector(`#${idPrefix}-message`).textContent = promptConfig.message;
        const buttonsEl = pageContainer.querySelector(`#${idPrefix}-buttons`);
        buttonsEl.innerHTML = ''; // Clear previous buttons
        promptConfig.buttons.forEach(buttonConfig => {
            const button = document.createElement('button');
            button.className = 'button-style button-secondary m-2';
            button.textContent = buttonConfig.text;
            button.addEventListener('click', () => {
                const event = new CustomEvent(buttonConfig.event, { detail: buttonConfig.detail });
                this.dom.appContainer.dispatchEvent(event);
            });
            buttonsEl.appendChild(button);
        });

        this.showPage(targetPageKey);
    }

    /**
     * Displays a toast notification for an unlocked achievement.
     * @param {object} achievement - The achievement object that was unlocked.
     */
    showAchievementToast(achievement) {
        const container = document.getElementById('achievement-toast-container');
        if (!container) return;

        const toastId = `toast-${Date.now()}`;
        const toastElement = document.createElement('div');
        toastElement.id = toastId;
        // Inspired by Xbox 360 achievement style
        toastElement.className = 'flex items-center bg-black bg-opacity-80 text-white p-3 rounded-lg shadow-lg border border-gray-600 animate-[achievement-toast-slide-in-out_6s_ease-in-out]';
        
        toastElement.innerHTML = `
            <div class="text-6xl mr-4">${achievement.icon}</div>
            <div>
                <p class="font-bold text-green-400">Achievement Unlocked</p>
                <p class="text-sm">${achievement.name}</p>
            </div>
        `;

        container.appendChild(toastElement);

        setTimeout(() => toastElement.remove(), 6000); // Remove after animation
    }

    // --- Tutorial & Guidance UI ---

    /**
     * Toggles a full-screen dimmer overlay.
     * @param {boolean} show - True to show the dimmer, false to hide it.
     * @param {string} [highlightSelector] - Optional CSS selector for an element to keep unobscured.
     */
    toggleDimmer(show, highlightSelector) {
        const dimmer = this.dom.dimmerOverlay;
        const highlightedElement = highlightSelector ? document.querySelector(highlightSelector) : null;

        // Reset styles first
        if (highlightedElement) {
            highlightedElement.style.zIndex = '';
            highlightedElement.style.position = '';
        }

        if (show) {
            dimmer.classList.add('active');
            if (highlightedElement) {
                highlightedElement.style.zIndex = '30'; // Bring it above the dimmer
                highlightedElement.style.position = 'relative'; // Needed for z-index to apply
            }
        } else {
            dimmer.classList.remove('active');
        }
    }

    /**
     * Shows a tutorial arrow pointing to a specific element.
     * @param {string} targetSelector - The CSS selector for the element to point to.
     * @param {string} position - The position of the arrow relative to the target ('top', 'bottom', 'left', 'right').
     */
    showArrow(targetSelector, position) {
        const target = document.querySelector(targetSelector);
        if (!target) return;

        this.hideArrow(); // Remove any existing arrow

        const arrow = document.createElement('div');
        arrow.id = 'tutorial-arrow-instance';
        arrow.className = `tutorial-arrow arrow-${position}`;
        this.dom.overlays.tutorial.appendChild(arrow);

        const targetRect = target.getBoundingClientRect();
        const overlayRect = this.dom.overlays.tutorial.getBoundingClientRect();

        // Position arrow relative to the target
        if (position === 'right') {
            arrow.style.top = `${targetRect.top + (targetRect.height / 2) - (arrow.offsetHeight / 2) - overlayRect.top}px`;
            arrow.style.left = `${targetRect.left - arrow.offsetWidth - 5 - overlayRect.left}px`;
        }
        // Add other positions (top, bottom, left) as needed
    }

    /**
     * Hides the tutorial arrow.
     * @returns {void}
     */
    hideArrow() {
        const existingArrow = document.getElementById('tutorial-arrow-instance');
        if (existingArrow) existingArrow.remove();
    }

    /**
     * Shows a highlight box around a specific element.
     * @param {string} targetSelector - The CSS selector for the element to highlight.
     * @returns {void}
     */
    showHighlightBox(targetSelector) {
        const target = document.querySelector(targetSelector);
        if (!target) return;

        this.hideHighlightBox(); // Remove any existing box

        const box = document.createElement('div');
        box.id = 'tutorial-highlight-box-instance';
        box.className = 'tutorial-highlight-box';
        this.dom.overlays.tutorial.appendChild(box);

        const targetRect = target.getBoundingClientRect();
        const overlayRect = this.dom.overlays.tutorial.getBoundingClientRect();

        // Position and size the box to match the target element
        box.style.top = `${targetRect.top - overlayRect.top - 4}px`; // -4 for border offset
        box.style.left = `${targetRect.left - overlayRect.left - 4}px`;
        box.style.width = `${targetRect.width + 8}px`;
        box.style.height = `${targetRect.height + 8}px`;
    }

    /**
     * Hides the highlight box.
     * @returns {void}
     */
    hideHighlightBox() {
        const existingBox = document.getElementById('tutorial-highlight-box-instance');
        if (existingBox) {
            existingBox.remove();
        }
    }

    /**
     * Generates the HTML for a single inventory indicator.
     * @param {'import' | 'export'} type - The type of indicator.
     * @param {number} quantity - The quantity of the item.
     * @param {object} masterItemsConfig - The master items config to find the display icon.
     * @param {string} [itemKey] - The key of the item (required for exports).
     * @returns {string} The HTML string for the indicator.
     * @private
     */
    _getIndicatorHtml(type, quantity, masterItemsConfig, itemKey) { 
        let displayIcon = type === 'import' ? '📥' : '📤'; // Default icons

        // Find the specific item's icon from the master config
        const itemConfig = Object.values(masterItemsConfig).flatMap(Object.values).find(item => item.key === itemKey);
        if (itemConfig) displayIcon = itemConfig.display || (type === 'import' ? '📥' : '📤');

        return `
            <div class="indicator indicator-${type}">
                <span class="indicator-icon">${displayIcon}</span>
                <span class="indicator-quantity">${quantity}</span>
            </div>
        `;
    }

    /**
     * Creates the UI for the player's collector bucket in Rolling Grid mode.
     * @param {number} totalGridSize - The total size of the grid.
     * @returns {void}
     */
    createCollector(totalGridSize) {
        const collectorContainer = document.createElement('div');
        collectorContainer.id = 'collector-container';
        collectorContainer.className = 'flex';
        collectorContainer.style.width = `${this.dom.grid.clientWidth}px`;
        collectorContainer.style.height = '350px'; // Give it a fixed height

        const gridCell = this.dom.grid.children[0];
        if (!gridCell) return;
        const cellWidth = gridCell.offsetWidth;

        const bucket = document.createElement('div');
        bucket.id = 'collector-bucket';
        // Use absolute positioning to move it left/right
        bucket.className = 'flex flex-col border-l-2 border-r-2 border-b-2 border-yellow-400 rounded-bl-lg rounded-br-lg p-1 bg-gray-900 absolute transition-transform duration-100 ease-linear';

        
        // The container is aligned with the full grid. We need to offset the bucket
        // to align with the start of the playable area.
        const playableAreaOffset = 2 * cellWidth;
        // Then, position the bucket under the correct column within that playable area.
        const x = playableAreaOffset + (Math.floor(totalGridSize/2) - 2) * cellWidth;
        bucket.style.transform = `translateX(${x}px)`;


        collectorContainer.style.top = (cellWidth*-2)+'px'; // Give it a fixed height
        // The bucket will hold up to 5 items, so we create 5 empty slots
        for (let i = 0; i < 5; i++) {
            const slot = document.createElement('div');
            slot.className = 'queue-gem'; // Reuse existing style for consistency
            slot.style.backgroundColor = '#374151'; // Empty slot color
            bucket.appendChild(slot);
        }

        collectorContainer.appendChild(bucket);

        // Insert the collector below the game grid
        this.dom.grid.parentNode.insertBefore(collectorContainer, this.dom.grid.nextSibling);
    }

    /**
     * Updates the visual state of the collector bucket.
     * @param {number} position - The column index of the collector.
     * @param {Array<object>} bucketContents - An array of items in the bucket.
     * @param {number} bufferSize - The grid's buffer size, for position calculation.
     * @returns {void}
     */
    updateCollector(position, bucketContents, bufferSize) {
        const bucketElement = document.getElementById('collector-bucket');
        if (!bucketElement) return;

        const gridCell = this.dom.grid.children[0];
        if (!gridCell) return;
        const cellWidth = gridCell.offsetWidth;

        // The container is aligned with the full grid. We need to offset the bucket
        // to align with the start of the playable area.
        const playableAreaOffset = bufferSize * cellWidth;
        // Then, position the bucket under the correct column within that playable area.
        const x = playableAreaOffset + (position - bufferSize) * cellWidth;
        bucketElement.style.transform = `translateX(${x}px)`;

        const slots = bucketElement.children;
        const bucketSize = bucketContents.length;
        for (let i = 0; i < 5; i++) {
            const slot = slots[4 - i]; // Fill from the bottom up
            const item = bucketContents[bucketSize - 1 - i];
            if (item) {
                slot.innerHTML = item.display;
                slot.style.backgroundColor = item.style.backgroundColor;
                slot.style.border = item.style.border || '';
            } else {
                slot.innerHTML = '';
                slot.style.backgroundColor = '#374151';
                slot.style.border = '';
            }
        }
    }

    /**
     * Creates and displays the RTS-style GUI for Settlers Mode.
     * @param {function(string): void} onSettlementSelected - Callback when a settlement icon is clicked.
     * @param {function(): void} onDeleteSelected - Callback when the delete button is clicked.
     * @param {function(): void} onPauseSelected - Callback when the pause button is clicked.
     */
    createRtsGui(onSettlementSelected, onDeleteSelected, onPauseSelected) {
        // Remove existing GUI if any
        if (this.rtsGuiElement) {
            this.rtsGuiElement.remove();
        }
    
        this.rtsGuiElement = document.createElement('div');
        this.rtsGuiElement.id = 'rts-gui';
        this.rtsGuiElement.className = 'fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 p-2 rounded-lg shadow-lg flex items-center z-50';
    
        const settlementsContainer = document.createElement('div');
        settlementsContainer.className = 'flex flex-wrap gap-2';

        // Add settlement buttons
        for (const key in this.masterItemsConfig.settlements) {
            const settlement = this.masterItemsConfig.settlements[key];
            const button = document.createElement('button');
            button.className = 'rts-gui-button';
            
            let displayContent = '';
            if (typeof settlement.display === 'object' && settlement.display.background) {
                displayContent = `
                    <div class="compound-emoji">
                        <span class="background">${settlement.display.background}</span>
                        <span class="foreground">${settlement.display.foreground || ''}</span>
                    </div>`;
            } else {
                displayContent = settlement.display || '';
            }
            button.innerHTML = displayContent;
            button.title = `Place ${settlement.name}`; // More descriptive tooltip
            button.dataset.key = key; // Store the settlement key

            button.addEventListener('click', () => onSettlementSelected(key));
            settlementsContainer.appendChild(button);
        }
    
        const contentContainer = document.createElement('div');
        contentContainer.id = 'rts-gui-content';
        contentContainer.className = 'flex space-x-2 items-center';
    
        contentContainer.appendChild(settlementsContainer);
    
        // Add delete button
        const deleteButton = document.createElement('button');
        deleteButton.className = 'rts-gui-button rts-gui-button-delete';
        deleteButton.innerHTML = '🗑️';
        deleteButton.title = 'Delete item from grid';
        deleteButton.addEventListener('click', onDeleteSelected);
        contentContainer.appendChild(deleteButton);
    
        // Add pause button
        const pauseButton = document.createElement('button');
        pauseButton.id = 'rts-gui-pause-button'; // ID for easy access to update icon
        pauseButton.className = 'rts-gui-button rts-gui-button-pause';
        pauseButton.innerHTML = '⏸️'; // Initial state: ready to pause
        pauseButton.title = 'Pause Game';
        pauseButton.addEventListener('click', onPauseSelected);
        contentContainer.appendChild(pauseButton);
    
        this.rtsGuiElement.appendChild(contentContainer);
    
        // Add the toggle button
        const toggleButton = document.createElement('button');
        toggleButton.id = 'rts-gui-toggle-button';
        toggleButton.className = 'rts-gui-button';
        toggleButton.innerHTML = '➡️'; // Icon for "collapse"
        toggleButton.title = 'Collapse Toolbar';
        toggleButton.addEventListener('click', () => this.rtsGuiElement.classList.toggle('collapsed'));
    
        this.rtsGuiElement.appendChild(toggleButton);
        this.dom.appContainer.appendChild(this.rtsGuiElement);
    }

    /**
     * Sets the visual state of the RTS GUI buttons to show which tool is active.
     * @param {'none' | 'place_settlement' | 'delete_cell'} tool - The current tool.
     * @param {string|null} key - The key of the settlement if placing.
     */
    setActiveRtsTool(tool, key) {
        if (!this.rtsGuiElement) return;

        const buttons = this.rtsGuiElement.querySelectorAll('.rts-gui-button');
        buttons.forEach(btn => btn.classList.remove('active'));

        if (tool === 'place_settlement' && key) {
            const targetButton = this.rtsGuiElement.querySelector(`[data-key="${key}"]`);
            if (targetButton) {
                targetButton.classList.add('active');
            }
        } else if (tool === 'delete_cell') {
            const deleteButton = this.rtsGuiElement.querySelector('.rts-gui-button-delete');
            if (deleteButton) {
                deleteButton.classList.add('active');
            }
        }
        // If tool is 'none', all buttons are already deactivated.
    }

    /**
     * Removes the RTS-style GUI from the DOM.
     */
    removeRtsGui() {
        if (this.rtsGuiElement) {
            this.rtsGuiElement.remove();
            this.rtsGuiElement = null;
        }
    }

    /**
     * Updates the icon and title of the RTS pause button.
     * @param {boolean} isPaused - True if the game is now paused.
     */
    updateRtsPauseButton(isPaused) {
        const pauseButton = document.getElementById('rts-gui-pause-button');
        if (pauseButton) {
            pauseButton.innerHTML = isPaused ? '▶️' : '⏸️';
            pauseButton.title = isPaused ? 'Resume Game' : 'Pause Game';
        }
    }


    /**
     * Sets up delegated event listeners on the NPC overlays for tooltips.
     * @private
     */
    _setupNpcTooltipListeners() {
        const overlays = [this.dom.npcOverlay, this.dom.nonHumanNPCOverlay];

        overlays.forEach(overlay => {
            if (!overlay) return;

            overlay.addEventListener('mouseover', (e) => {
                const npcElement = e.target.closest('.npc, .non-human-npc');
                if (npcElement) {
                    const npcId = parseInt(npcElement.id.split('-').pop(), 10);
                    const managerType = npcElement.classList.contains('npc') ? 'human' : 'non-human';
                    // Request the full NPC data from the Game class
                    this.eventEmitter.emit('ui:requestNpcData', { npcId, managerType });
                    // Pause the specific NPC being hovered over
                    this.eventEmitter.emit('game:hoverNpc', { npcId, managerType, isHovering: true });
                }
            });

            overlay.addEventListener('mouseout', (e) => {
                const npcElement = e.target.closest('.npc, .non-human-npc');
                if (npcElement) {
                    const npcId = parseInt(npcElement.id.split('-').pop(), 10);
                    const managerType = npcElement.classList.contains('npc') ? 'human' : 'non-human';

                    // Hide the tooltip and unpause the NPC
                    this.hideNpcTooltip();
                    this.eventEmitter.emit('game:hoverNpc', { npcId, managerType, isHovering: false });
                }
            });
        });
    }

    /**
     * Creates and displays a tooltip for a specific NPC.
     * @param {import('../models/Character.js').Character} npc - The NPC data object.
     * @param {string} managerType - The type of manager ('human' or 'non-human').
     */
    showNpcTooltip(npc, managerType) {
        this.hideNpcTooltip(); // Ensure no other tooltips are active

        const tooltip = document.createElement('div');
        tooltip.className = 'npc-tooltip';
        tooltip.innerHTML = this._getNpcTooltipHtml(npc);

        this.dom.overlays.tooltip.appendChild(tooltip);

        this.activeNpcTooltip = {
            element: tooltip,
            npcId: npc.id,
            managerType: managerType
        };

        this._updateNpcTooltipPosition();
    }

    /**
     * Hides and removes the active NPC tooltip.
     */
    hideNpcTooltip() {
        if (this.activeNpcTooltip) {
            this.activeNpcTooltip.element.remove();
            this.activeNpcTooltip = null;
        }
    }

    /**
     * Toggles the visibility of the game stats box.
     * @param {boolean} show - True to show, false to hide.
     */
    toggleStatsBox(show) {
        this.dom.gameStatsBox.classList.toggle('hidden', !show);
    }

    /**
     * Updates the live game stats panel with the latest data.
     * @param {object} stats - An object containing game statistics.
     */
    updateGameStats(stats) {
        const contentEl = this.dom.gameStatsContent;
        if (!contentEl) return;

        // Using a template literal for a clean update.
        // This is efficient enough for this use case.
        contentEl.innerHTML = `
            <div class="flex justify-between"><span>Biome:</span> <span class="font-bold">${stats.biomeName}</span></div>
            <div class="flex justify-between"><span>Grid Size:</span> <span class="font-bold">${stats.gridSize}</span></div>
            <div class="flex justify-between"><span>Settlements:</span> <span class="font-bold">${stats.settlements}</span></div>
            <div class="flex justify-between"><span>Villagers:</span> <span class="font-bold">${stats.humanNpcs}</span></div>
            <div class="flex justify-between"><span>Creatures:</span> <span class="font-bold">${stats.nonHumanNpcs}</span></div>
            <div class="flex justify-between"><span>Animals:</span> <span class="font-bold">${stats.animals}</span></div>
            <div class="flex justify-between"><span>Items on Grid:</span> <span class="font-bold">${stats.gems}</span></div>
            <div class="flex justify-between"><span>Empty Cells:</span> <span class="font-bold">${stats.emptyCells}</span></div>
        `;
    }

    /**
     * Populates the NPC selector dropdown with the current list of NPCs.
     * @param {Array<object>} allNpcs - A combined array of all human and non-human NPCs.
     */
    updateNpcSelector(allNpcs) {
        const selector = this.dom.npcSelector;
        const selectedValue = selector.value;

        selector.innerHTML = '<option value="">Select an NPC</option>'; // Reset with a placeholder

        allNpcs.sort((a, b) => a.id - b.id).forEach(npc => {
            const option = document.createElement('option');
            option.value = npc.id;
            option.dataset.managerType = npc.managerType;
            const status = npc.isEx ? ' (Ex-Inhabitant)' : ' (On Grid)';
            option.textContent = `${npc.config.display} ${npc.attributes.nickname || npc.config.name} (ID: ${npc.id})${status}`;
            selector.appendChild(option);
        });

        // Restore previous selection if it still exists
        if (selectedValue) {
            selector.value = selectedValue;
        }
    }

    /**
     * Displays an editor for the selected NPC's properties.
     * @param {import('../models/Character.js').Character} npc - The selected NPC object.
     * @param {object} allNpcConfigs - A map of all possible NPC configurations.
     */
    displayNpcEditor(npc, allNpcConfigs) {
        const contentEl = this.dom.npcEditorContent;
        if (!npc) {
            contentEl.classList.add('hidden');
            return;
        }

        // Clear any existing highlight first.
        this._clearNpcEditorHighlight();

        // If the NPC is on the grid (not an ex-inhabitant), highlight them.
        if (!npc.isEx) {
            const idPrefix = npc.managerType === 'human' ? 'npc-' : 'non-human-npc-';
            const npcElementId = `${idPrefix}${npc.id}`;
            const npcElement = document.getElementById(npcElementId);
            if (npcElement) {
                npcElement.classList.add('npc-editor-highlight');
            }
        }


        // Store the current NPC being edited to access its data on save.
        contentEl.dataset.editingNpcId = npc.id;
        contentEl.dataset.editingNpcManagerType = npc.managerType;

        // --- Options for Dropdowns ---

        const skinToneOptions = [
            { value: '', text: 'Default' },
            { value: '🏻', text: 'Light' },
            { value: '🏼', text: 'Medium-Light' },
            { value: '🏽', text: 'Medium' },
            { value: '🏾', text: 'Medium-Dark' },
            { value: '🏿', text: 'Dark' }
        ].map(st => `<option value="${st.value}" ${npc.attributes.skinTone === st.value ? 'selected' : ''}>${st.text}</option>`).join('');

        const accessoryOptions = [
            '<option value="">None</option>',
            ...Object.values(this.masterItemsConfig.accessories).map(acc => {
                const isSelected = npc.inventory.accessory?.key === acc.key ? 'selected' : '';
                return `<option value="${acc.key}" ${isSelected}>${acc.display} ${acc.name}</option>`;
            })
        ].join('');

        const characterOptions = Object.values(allNpcConfigs).map(config => {
            const isSelected = config.key === npc.config.key ? 'selected' : '';
            return `<option value="${config.key}" ${isSelected}>${config.display} ${config.name}</option>`;
        }).join('');

        contentEl.innerHTML = `
            <div class="space-y-1 text-sm">
                <div>
                    <label for="npc-nickname-editor" class="block text-xs font-medium text-gray-400">Nickname</label>
                    <input type="text" id="npc-nickname-editor" value="${npc.attributes.nickname || ''}" class="w-full bg-gray-900 border border-gray-600 rounded p-1">
                </div>
                <div>
                    <label for="npc-character-editor" class="block text-xs font-medium text-gray-400">Character Type</label>
                    <select id="npc-character-editor" class="w-full bg-gray-900 border border-gray-600 rounded p-1">
                        ${characterOptions}
                    </select>
                </div>
                 <div>
                    <label for="npc-age-editor" class="block text-xs font-medium text-gray-400">Age</label>
                    <input type="number" id="npc-age-editor" value="${npc.attributes.age || 0}" class="w-full bg-gray-900 border border-gray-600 rounded p-1">
                </div>
                <div>
                    <label for="npc-hunger-editor" class="block text-xs font-medium text-gray-400">Hunger</label>
                    <input type="number" id="npc-hunger-editor" value="${npc.attributes.hunger || 0}" class="w-full bg-gray-900 border border-gray-600 rounded p-1">
                </div>
                <div>
                    <label for="npc-love-editor" class="block text-xs font-medium text-gray-400">In Love With (ID)</label>
                    <input type="number" id="npc-love-editor" value="${npc.attributes.isInLoveWith || ''}" placeholder="None" class="w-full bg-gray-900 border border-gray-600 rounded p-1">
                </div>
                <div>
                    <label for="npc-role-editor" class="block text-xs font-medium text-gray-400">Role</label>
                    <select id="npc-role-editor" class="w-full bg-gray-900 border border-gray-600 rounded p-1">
                        <option value="wandering" ${npc.state.role === 'wandering' ? 'selected' : ''}>Wandering</option>
                        <option value="distributor" ${npc.state.role === 'distributor' ? 'selected' : ''}>Distributor</option>
                        <option value="hauler" ${npc.state.role === 'hauler' ? 'selected' : ''}>Hauler</option>
                    </select>
                </div>
                <div class="flex items-center">
                    <input type="checkbox" id="npc-leaving-editor" ${npc.state.isLeaving ? 'checked' : ''} class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                    <label for="npc-leaving-editor" class="ml-2 block text-sm text-gray-300">Is Leaving</label>
                </div>
                <div>
                    <label for="npc-skintone-editor" class="block text-xs font-medium text-gray-400">Skin Tone</label>
                    <select id="npc-skintone-editor" class="w-full bg-gray-900 border border-gray-600 rounded p-1" ${!npc.config.supportsSkinTone ? 'disabled' : ''}>
                        ${skinToneOptions}
                    </select>
                </div>
                <div>
                    <label for="npc-accessory-editor" class="block text-xs font-medium text-gray-400">Accessory</label>
                    <select id="npc-accessory-editor" class="w-full bg-gray-900 border border-gray-600 rounded p-1">
                        ${accessoryOptions}
                    </select>
                </div>
                <div class="flex gap-2 mt-4">
                    <button id="npc-editor-save-btn" class="button-style button-primary w-full">Save</button>
                    <button id="npc-editor-control-btn" class="button-style button-secondary w-full">Control</button>
                    <button id="npc-editor-delete-btn" class="button-style button-danger w-full">Delete</button>
                </div>
            </div>
        `;

        contentEl.classList.remove('hidden');

        // Add event listeners for the new input fields
        const saveButton = document.getElementById('npc-editor-save-btn');
        if (saveButton && !saveButton.dataset.listenerAttached) {
            saveButton.addEventListener('click', () => {
                const npcId = parseInt(contentEl.dataset.editingNpcId, 10);
                const managerType = contentEl.dataset.editingNpcManagerType;
                if (!npcId || !managerType) return;

                const propertiesToUpdate = [
                    { property: 'nickname', value: document.getElementById('npc-nickname-editor').value, type: 'string' },
                    { property: 'character', value: document.getElementById('npc-character-editor').value, type: 'string' },
                    { property: 'age', value: document.getElementById('npc-age-editor').value, type: 'number' },
                    { property: 'hunger', value: document.getElementById('npc-hunger-editor').value, type: 'number' },
                    { property: 'isInLoveWith', value: document.getElementById('npc-love-editor').value, type: 'number' },
                    { property: 'role', value: document.getElementById('npc-role-editor').value, type: 'string' },
                    { property: 'isLeaving', value: document.getElementById('npc-leaving-editor').checked, type: 'boolean' },
                    { property: 'skinTone', value: document.getElementById('npc-skintone-editor').value, type: 'string' },
                    { property: 'accessory', value: document.getElementById('npc-accessory-editor').value, type: 'string' }
                ];

                this.eventEmitter.emit('game:batchUpdateNpcProperties', {
                    npcId,
                    managerType,
                    updates: propertiesToUpdate
                });

                // Collapse the editor after saving and reset the dropdown
                this.dom.npcEditorContent.classList.add('hidden');
                this.dom.npcSelector.value = '';
            });
            saveButton.dataset.listenerAttached = 'true';
        }

        const controlButton = document.getElementById('npc-editor-control-btn');
        if (controlButton && !controlButton.dataset.listenerAttached) {
            controlButton.addEventListener('click', () => {
                const npcId = parseInt(contentEl.dataset.editingNpcId, 10);
                const managerType = contentEl.dataset.editingNpcManagerType;
                if (!npcId || !managerType) return;

                this.eventEmitter.emit('game:controlNpc', { npcId, managerType });

                // Collapse the editor and reset dropdowns after taking control
                this.dom.npcEditorContent.classList.add('hidden');
                this.dom.npcSelector.value = '';
            });
            controlButton.dataset.listenerAttached = 'true';
        }

        const deleteButton = document.getElementById('npc-editor-delete-btn');
        if (deleteButton && !deleteButton.dataset.listenerAttached) {
            deleteButton.addEventListener('click', () => {
                const npcId = parseInt(contentEl.dataset.editingNpcId, 10);
                const managerType = contentEl.dataset.editingNpcManagerType;
                if (!npcId || !managerType) return;

                this.eventEmitter.emit('game:deleteNpc', { npcId, managerType });
                this.dom.npcEditorContent.classList.add('hidden');
                this.dom.npcSelector.value = '';
            });
            deleteButton.dataset.listenerAttached = 'true';
        }
    }

    /**
     * Removes the highlight class from any NPC that currently has it.
     * @private
     */
    _clearNpcEditorHighlight() {
        const highlightedNpc = document.querySelector('.npc-editor-highlight');
        if (highlightedNpc) {
            highlightedNpc.classList.remove('npc-editor-highlight');
        }
    }

    /**
     * Resets the tracking of rendered NPCs, forcing a full redraw on the next update cycle.
     * This is useful for debugging visual glitches where NPCs might disappear.
     */
    kickstartNpcRendering() {
        this.renderedEntityIdSets.clear();
        this.debugManager.log('NPC rendering kickstarted. Forcing full redraw on next tick.');
    }

    /**
     * Populates the settlement selector dropdown with the current list of settlements.
     * @param {Array<object>} allSettlements - An array of all active settlement objects.
     */
    updateSettlementSelector(allSettlements) {
        const selector = this.dom.settlementSelector;
        const selectedValue = selector.value;

        selector.innerHTML = '<option value="">Select a Settlement</option>'; // Reset

        allSettlements.sort((a, b) => a.name.localeCompare(b.name)).forEach(settlement => {
            const option = document.createElement('option');
            option.value = `${settlement.position.r},${settlement.position.c}`;
            option.textContent = `${settlement.display.background} ${settlement.name} (${settlement.position.r},${settlement.position.c})`;
            selector.appendChild(option);
        });

        if (selectedValue) {
            selector.value = selectedValue;
        }
    }

    /**
     * Displays an editor for the selected settlement's properties.
     * This has been refactored to use a modal dialog. The old dev panel is no longer used for this.
     * @param {import('../models/Settlement.js').Settlement} settlement - The settlement object to edit.
     */
    displaySettlementEditor(settlement) {
        const modal = this.dom.settlementEditorModal; // This is the new modal element
        const titleEl = this.dom.settlementEditorModalTitle; // Title element in the modal
        const tabsEl = this.dom.settlementEditorModalTabs; // Tabs container in the modal
        const contentEl = this.dom.settlementEditorModalContent; // Content area in the modal

        if (!settlement) {
            modal.classList.add('hidden');
            return;
        }

        const settlementKey = `${settlement.position.r},${settlement.position.c}`;
        modal.dataset.editingSettlementKey = settlementKey; // Store key on the modal itself
        titleEl.textContent = settlement.name;

        let possibleImports = new Set();
        let possibleExports = new Set();

        if (settlement.isUniversal) {
            // Warehouses can store anything. List all accessories as both importable and exportable.
            Object.keys(this.masterItemsConfig.accessories).forEach(key => {
                possibleImports.add(key);
                possibleExports.add(key);
            });
        } else if (settlement.acceptsNoActionItems) {
            // Recycler: "Imports" are all recyclable items, "Exports" are defined in its recipe (e.g., coins).
            const allSettlementConfigs = Object.values(this.masterItemsConfig.settlements);
            const allImportKeys = new Set();
            allSettlementConfigs.forEach(s => {
                if (s.recipes) {
                    s.recipes.forEach(r => {
                        if (r.imports) Object.keys(r.imports).forEach(key => allImportKeys.add(key));
                    });
                }
            });

            Object.values(this.masterItemsConfig.accessories).forEach(acc => {
                const isAnImport = allImportKeys.has(acc.key);
                const hasAction = acc.type && npcActionsConfig[acc.type];
                if (!isAnImport && !hasAction) {
                    possibleImports.add(acc.key);
                }
            });

            if (settlement.recipes) {
                settlement.recipes.forEach(recipe => {
                    if (recipe.exports) Object.keys(recipe.exports).forEach(key => possibleExports.add(key));
                });
            }
        } else if (settlement.recipes) {
            // Standard production buildings: derive from recipes.
            settlement.recipes.forEach(recipe => {
                if (recipe.imports) Object.keys(recipe.imports).forEach(key => possibleImports.add(key));
                if (recipe.exports) Object.keys(recipe.exports).forEach(key => possibleExports.add(key));
            });
        }

        // --- Tab Content Generation Logic ---
        const createInventoryField = (itemKey, type) => {
            const itemConfig = this.masterItemsConfig.accessories[itemKey];
            if (!itemConfig) return '';
            // Use the correct inventory bucket based on the type
            const currentQty = type === 'import' ? (settlement.imports[itemKey] || 0) : (settlement.exports[itemKey] || 0);
            return `
                <div class="flex items-center justify-between">
                    <label for="settlement-inv-${type}-${itemKey}" class="text-xs font-medium text-gray-400">${itemConfig.display} ${itemConfig.name}</label>
                    <input type="number" id="settlement-inv-${type}-${itemKey}" value="${currentQty}" class="w-16 bg-gray-900 border border-gray-600 rounded p-1 text-right">
                </div>
            `;
        };

        // Define the content for each tab
        const tabs = {
            'Summary': `
                <div class="space-y-2">
                    <p class="text-sm text-gray-400">Current production cooldown: ${settlement.cooldown} ticks</p>
                    <div class="flex items-center mt-4">
                        <input type="checkbox" id="settlement-imports-paused-editor" ${settlement.importsPaused ? 'checked' : ''} class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                        <label for="settlement-imports-paused-editor" class="ml-2 block text-sm text-gray-300">Pause All Imports</label>
                    </div>
                    <div class="flex items-center mt-1">
                        <input type="checkbox" id="settlement-exports-paused-editor" ${settlement.exportsPaused ? 'checked' : ''} class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                        <label for="settlement-exports-paused-editor" class="ml-2 block text-sm text-gray-300">Pause All Exports</label>
                    </div>
                </div>
            `,
            'Imports': `
                <div class="space-y-1 text-sm">
                    ${Array.from(possibleImports).map(key => createInventoryField(key, 'import')).join('') || '<p class="text-xs text-gray-500">No defined imports.</p>'}
                </div>
            `,
            'Exports': `
                <div class="space-y-1 text-sm">
                    ${Array.from(possibleExports).map(key => createInventoryField(key, 'export')).join('') || '<p class="text-xs text-gray-500">No defined exports.</p>'}
                </div>
            `,
            'Stats': `
                <p class="text-sm text-gray-400">Statistics for this settlement are not yet tracked.</p>
            `
        };

        // --- Render Tabs and Set Up Click Handlers ---
        tabsEl.innerHTML = Object.keys(tabs).map((tabName, index) => `
            <button class="settlement-tab-btn px-4 py-2 text-sm font-medium ${index === 0 ? 'border-b-2 border-yellow-400 text-yellow-400' : 'text-gray-400 hover:text-white'}" data-tab="${tabName}">
                ${tabName}
            </button>
        `).join('');

        const tabButtons = tabsEl.querySelectorAll('.settlement-tab-btn');
        const showTab = (tabName) => {
            contentEl.innerHTML = tabs[tabName];
            tabButtons.forEach(btn => {
                if (btn.dataset.tab === tabName) {
                    btn.classList.add('border-b-2', 'border-yellow-400', 'text-yellow-400');
                    btn.classList.remove('text-gray-400', 'hover:text-white');
                } else {
                    btn.classList.remove('border-b-2', 'border-yellow-400', 'text-yellow-400');
                    btn.classList.add('text-gray-400', 'hover:text-white');
                }
            });
        };

        tabsEl.onclick = (e) => {
            const button = e.target.closest('.settlement-tab-btn');
            if (button) {
                showTab(button.dataset.tab);
            }
        };

        // Show the first tab by default
        showTab(Object.keys(tabs)[0]);

        // Ensure there's only one save button and it's in the right place
        let saveButton = modal.querySelector('#settlement-editor-save-btn');
        if (saveButton) saveButton.remove(); // Remove old one if it exists
        
        // The save button should be outside the scrollable content area
        contentEl.insertAdjacentHTML('afterend', '<div id="settlement-editor-footer" class="mt-4"></div>');
        modal.querySelector('#settlement-editor-footer').innerHTML = '<button id="settlement-editor-save-btn" class="button-style button-primary w-full">Save Changes</button>';

        saveButton = modal.querySelector('#settlement-editor-save-btn');
        // --- Save Logic ---
        if (saveButton && !saveButton.dataset.listenerAttached) {
            saveButton.addEventListener('click', () => {
                const key = modal.dataset.editingSettlementKey;
                if (!key) return;

                // The pause checkboxes are always present in the modal's scope, but we need to find them.
                const importsPausedCheckbox = document.getElementById('settlement-imports-paused-editor');
                const exportsPausedCheckbox = document.getElementById('settlement-exports-paused-editor');

                // Since tabs are re-rendered, we must read values from the inputs that *might* exist in the DOM
                // before they are potentially removed by a tab switch.
                const updates = [
                    { property: 'importsPaused', value: importsPausedCheckbox ? importsPausedCheckbox.checked : settlement.importsPaused },
                    { property: 'exportsPaused', value: exportsPausedCheckbox ? exportsPausedCheckbox.checked : settlement.exportsPaused }
                ];

                // Collect import updates
                possibleImports.forEach(itemKey => {
                    // Find the input within the modal, regardless of the current tab
                    const input = modal.querySelector(`#settlement-inv-import-${itemKey}`);
                    if (input) {
                        updates.push({ property: `imports.${itemKey}`, value: parseInt(input.value, 10) });
                    }
                });

                // Collect export updates
                possibleExports.forEach(itemKey => {
                    const input = modal.querySelector(`#settlement-inv-export-${itemKey}`);
                    if (input) {
                        updates.push({ property: `exports.${itemKey}`, value: parseInt(input.value, 10) });
                    }
                });

                this.eventEmitter.emit('game:batchUpdateSettlementProperties', {
                    settlementKey: key,
                    updates: updates
                });

                // Close the modal after saving
                modal.classList.add('hidden');
                // Clean up the footer to prevent duplicate buttons on next open
                const footer = modal.querySelector('#settlement-editor-footer');
                if (footer) footer.remove();
            });
            saveButton.dataset.listenerAttached = 'true';
        }

        // Add listener for the close button
        if (!this.dom.settlementEditorModalCloseBtn.dataset.listenerAttached) {
            this.dom.settlementEditorModalCloseBtn.addEventListener('click', () => modal.classList.add('hidden'));
            this.dom.settlementEditorModalCloseBtn.dataset.listenerAttached = 'true';
        }

        modal.classList.remove('hidden');
    }


    /**
     * Displays a list of accessories in the dev tools modal.
     * @param {string} title - The title for the modal window.
     * @param {Array<object>|Object<string, number>} items - The items to display. Can be an array of accessory objects or an object mapping keys to quantities.
     * @param {boolean} [isCountMap=false] - True if the items are an object map with counts.
     * @private
     */
    _displayAccessoryListInModal(title, items, isCountMap = false) {
        this.dom.devToolsModalTitle.textContent = title;
        const contentEl = this.dom.devToolsModalContent;

        let itemsHtml;
        if (isCountMap) {
            itemsHtml = Object.entries(items).map(([key, quantity]) => {
                const itemConfig = this.masterItemsConfig.accessories[key];
                if (!itemConfig) return '';
                return `
                    <div class="flex items-center justify-between p-2 border-b border-gray-600">
                        <span>${itemConfig.display} ${itemConfig.name}</span>
                        <span class="font-bold">${quantity}</span>
                    </div>`;
            }).join('');
        } else {
            itemsHtml = items.map(item => `
                <div class="flex items-center p-2 border-b border-gray-600">
                    <span>${item.display} ${item.name}</span>
                </div>`).join('');
        }

        contentEl.innerHTML = itemsHtml || '<p class="text-gray-400">No items to display.</p>';
        this.dom.devToolsModal.classList.remove('hidden');
    }




    /**
     * Updates the position of the active NPC tooltip to follow the NPC.
     * @private
     */
    _updateNpcTooltipPosition() {
        if (!this.activeNpcTooltip) return;

        const { element: tooltip, npcId, managerType } = this.activeNpcTooltip;
        const idPrefix = managerType === 'human' ? 'npc-' : 'non-human-npc-';
        const npcElement = document.getElementById(`${idPrefix}${npcId}`);

        if (!npcElement) {
            this.hideNpcTooltip();
            return;
        }

        const npcRect = npcElement.getBoundingClientRect();
        const overlayRect = this.dom.overlays.tooltip.getBoundingClientRect();

        // Adjust offset to account for the tooltip's new tail (10px) and add some extra space (10px)
        const top = npcRect.top - overlayRect.top - tooltip.offsetHeight - 20;
        const left = npcRect.left - overlayRect.left + (npcRect.width / 2) - (tooltip.offsetWidth / 2);

        tooltip.style.transform = `translate(${left}px, ${top}px)`;
    }

    /**
     * Generates the HTML content for an NPC's tooltip.
     * @param {import('../models/Character.js').Character} npc - The NPC data object.
     * @returns {string} The HTML content for the tooltip.
     * @private
     */
    _getNpcTooltipHtml(npc) {
        let html = `<div class="font-bold text-lg">${npc.attributes.nickname || npc.config.name}</div>`;
        html += `<div class="text-sm text-gray-400">${npc.config.description}</div><hr class="my-1 border-gray-500">`;

        if (npc.state.role) { html += `<div>Role: <span class="font-semibold">${npc.state.role}</span></div>`; }
        if (npc.state.currentAction) { html += `<div>Action: <span class="font-semibold">${npc.state.currentAction}</span></div>`; }
        if (npc.inventory.accessory) { html += `<div>Carrying: <span class="font-semibold">${npc.inventory.accessory.name}</span></div>`; }
        if (npc.attributes.age !== undefined) { html += `<div>Age: <span class="font-semibold">${Math.floor(npc.attributes.age)}</span></div>`; }
        if (npc.attributes.isInLoveWith !== null) { html += `<div>❤️ In love</div>`; }

        return html;
    }


    // --- Spotting Mode ---

    /**
     * Toggles the binocular-style spotting mode on and off.
     */
    toggleSpottingMode() {
        this.isSpottingModeActive = !this.isSpottingModeActive;
        const appContainer = this.dom.appContainer; // The root container for all UI

        if (this.isSpottingModeActive) {
            // Hide the cursor on the root element to ensure it's hidden everywhere.
            document.documentElement.style.cursor = 'crosshair';
            this.spottingOverlay.classList.add('active');
            appContainer.classList.add('spotting-active'); // Apply class to the root container
            document.addEventListener('click', this.boundHandleSpottingClick, true); // Use capture phase
            document.addEventListener('mousemove', this.boundHandleSpottingMouseMove);
            this.playAudio('click');
        } else {
            // Restore the default cursor on the root element.
            document.documentElement.style.cursor = '';
            this.spottingOverlay.classList.remove('active');
            appContainer.classList.remove('spotting-active'); // Remove class from the root container
            this.dom.gameGridWrapper.style.transform = ''; // Reset grid transform specifically
            document.removeEventListener('click', this.boundHandleSpottingClick, true);
            document.removeEventListener('mousemove', this.boundHandleSpottingMouseMove);
        }
    }

    /**
     * Handles mouse movement to pan the zoomed-in grid during spotting mode.
     * @param {MouseEvent} e - The mouse move event.
     * @private
     */
    _handleSpottingMouseMove(e) {
        if (!this.isSpottingModeActive) return;

        const gridWrapper = this.dom.gameGridWrapper;
        const overlay = this.spottingOverlay;

        const { clientX, clientY } = e;
        const { innerWidth, innerHeight } = window;

        // --- Pan the Grid ---
        // Calculate mouse position as a percentage from 0 to 1
        const panX = clientX / innerWidth;
        const panY = clientY / innerHeight;

        // With a scale of 3, the grid is 3x its size. The "pannable" area is the extra 200% (or 2x the grid size).
        // We want to map the mouse position across the screen to this pannable area.
        // The total translation range is from +100% to -100% of the grid's original size.
        const zoomLevel = 3;
        const pannableRange = zoomLevel - 1; // e.g., scale(3) -> 200% extra -> range is 2

        const translateX = -(panX * pannableRange - pannableRange / 2) * 100;
        const translateY = -(panY * pannableRange - pannableRange / 2) * 100;

        gridWrapper.style.transform = `translate(${translateX}%, ${translateY}%) scale(${zoomLevel})`;

        // --- Update Binocular Position ---
        // This makes the "lenses" follow the cursor.
        overlay.style.setProperty('--mouse-x', `${clientX}px`);
        overlay.style.setProperty('--mouse-y', `${clientY}px`);
    }

    /**
     * Handles clicks during spotting mode to tag NPCs.
     * @param {MouseEvent} e - The mouse click event.
     * @private
     */
    _handleSpottingClick(e) {
        if (!this.isSpottingModeActive) return;

        // Prevent any other click handlers from running.
        e.stopPropagation();
        e.preventDefault();

        const clickX = e.clientX;
        const clickY = e.clientY;

        let clickedNpc = null;
        let minDistance = Infinity;

        // Find the closest NPC to the click coordinates.
        const npcElements = document.querySelectorAll('.npc, .non-human-npc');
        npcElements.forEach(el => {
            const rect = el.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const distance = Math.sqrt(Math.pow(clickX - centerX, 2) + Math.pow(clickY - centerY, 2));

            // Check if the click is within the NPC's bounds and closer than any previous NPC.
            if (distance < (rect.width / 2) && distance < minDistance) {
                minDistance = distance;
                const npcId = parseInt(el.id.split('-').pop(), 10);
                const managerType = el.classList.contains('npc') ? 'human' : 'non-human';
                clickedNpc = { npcId, managerType };
            }
        });

        if (clickedNpc) {
            this.eventEmitter.emit('ui:npcTagged', clickedNpc);
        }
    }
}